import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  TextField,
  Stack,
  Menu,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import axiosInstance from "../../axios/axiosInstance";
import { ConfirmDialog } from "./ConfirmDialog";
import ProposedSlots from "./ProposedSlots";
import CloseIcon from "@mui/icons-material/Close";
import CareerJourneyPage from "../career_journey/CareerJourneyPage";
import VerifiedIcon from "@mui/icons-material/Verified";
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3500";

const JobApplications = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [selectedInterviewForDecision, setSelectedInterviewForDecision] =
    useState(null);
  const [decisionValue, setDecisionValue] = useState("");
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedInterviewForSchedule, setSelectedInterviewForSchedule] =
    useState(null);
  const [stages, setStages] = useState([
    {
      stageName: "",
      date: "",
      time: "",
      interviewers: [""],
      stageFeedback: null,
      candidateStageFeedback: "",
      isProposed: true,
      isNewStage: true,
    },
  ]);
  const [editingInterviewer, setEditingInterviewer] = useState(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [selectedStageForFeedback, setSelectedStageForFeedback] =
    useState(null);
  const [stageFeedback, setStageFeedback] = useState("");
  const [candidateStageFeedback, setCandidateStageFeedback] = useState("");
  const [stageCompleteDialogOpen, setStageCompleteDialogOpen] = useState(false);
  const [selectedStageForComplete, setSelectedStageForComplete] =
    useState(null);
  const [overallFeedbackDialogOpen, setOverallFeedbackDialogOpen] =
    useState(false);
  const [overallFeedback, setOverallFeedback] = useState("");
  const [overallCandidateFeedback, setOverallCandidateFeedback] = useState("");
  const [overallDecision, setOverallDecision] = useState("");
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedInterviewForAction, setSelectedInterviewForAction] =
    useState(null);

  // Propose Time Slots Modal States
  const [proposeSlotDialogOpen, setProposeSlotDialogOpen] = useState(false);
  const [selectedInterviewForProposeSlot, setSelectedInterviewForProposeSlot] =
    useState(null);
  // Per-stage proposed dates: { stageIndex: [{ date, timeSlots }] }
  const [proposedDatesByStage, setProposedDatesByStage] = useState({});

  // Schedule Dialog States - for Add New Round and Reschedule
  // Per-stage schedule dates: { stageIndex: [{ date, timeSlots }] }
  const [scheduleDatesByStage, setScheduleDatesByStage] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmProps, setConfirmProps] = useState({
    title: "",
    message: "",
    onConfirm: null,
    confirmLabel: "Confirm",
    cancelLabel: "Cancel",
  });

  const [careerDialogOpen, setCareerDialogOpen] = useState(false);
  const [selectedCareerJourneyId, setSelectedCareerJourneyId] = useState(null);
  const [selectedPeerReview, setSelectedPeerReview] = useState(false);

  const handleOpenCareerJourney = (interview) => {
    console.log("inside the click", interview);
    setSelectedCareerJourneyId(interview?.candidate?.id);
    setSelectedPeerReview(interview?.candidate?.peerReview);
    setCareerDialogOpen(true);
  };

  const handleCloseCareerJourney = () => {
    setCareerDialogOpen(false);
    setSelectedCareerJourneyId(null);
  };

  const openConfirm = ({
    title,
    message,
    onConfirm,
    confirmLabel,
    cancelLabel,
  }) => {
    setConfirmProps({ title, message, onConfirm, confirmLabel, cancelLabel });
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    // clear callback to avoid accidental reuse
    setConfirmProps((p) => ({ ...p, onConfirm: null }));
  };

  const token = Cookies.get("token");

  useEffect(() => {
    fetchInterviews();
  }, []);

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/api/job-postings/interviews/corporate`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.interviews) {
          setInterviews(data.interviews);
        }
      }
    } catch (error) {
      console.error("Error fetching interviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCandidateName = (candidate) => {
    if (!candidate) return "N/A";
    const firstName = candidate.firstName || "";
    const lastName = candidate.lastName || "";
    return `${firstName} ${lastName}`.trim() || candidate.email || "N/A";
  };

  const getActiveStage = (interview) => {
    if (interview.status === "accepted" || interview.status === "requested")
      return null;
    if (
      !interview ||
      !Array.isArray(interview.stages) ||
      interview.stages.length === 0
    ) {
      return null;
    }

    // Fix: For slot_selected or in_progress, always show the latest selected stage
    if (
      interview.status === "slot_selected" ||
      interview.status === "in_progress"
    ) {
      const selectedStages = interview.stages.filter(
        (s) =>
          s.candidateAction === "selected" || s.candidateAction === "accepted",
      );
      if (selectedStages.length > 0) {
        return selectedStages[selectedStages.length - 1];
      }
    }

    // Fix: For slot_selected or in_progress, always show the latest selected stage
    if (
      interview.status === "slot_selected" ||
      interview.status === "in_progress"
    ) {
      const selectedStages = interview.stages.filter(
        (s) =>
          s.candidateAction === "selected" || s.candidateAction === "accepted",
      );
      if (selectedStages.length > 0) {
        return selectedStages[selectedStages.length - 1];
      }
    }

    // Fix: For slot_selected or in_progress, always show the latest selected stage
    if (
      interview.status === "slot_selected" ||
      interview.status === "in_progress"
    ) {
      const selectedStages = interview.stages.filter(
        (s) =>
          s.candidateAction === "selected" || s.candidateAction === "accepted",
      );
      if (selectedStages.length > 0) {
        return selectedStages[selectedStages.length - 1];
      }
    }

    // Fix: For slot_selected or in_progress, always show the latest selected stage
    if (
      interview.status === "slot_selected" ||
      interview.status === "in_progress"
    ) {
      const selectedStages = interview.stages.filter(
        (s) =>
          s.candidateAction === "selected" || s.candidateAction === "accepted",
      );
      if (selectedStages.length > 0) {
        return selectedStages[selectedStages.length - 1];
      }
    }

    // Build a feedback map keyed by stageName
    const feedbackMap = {};
    (interview.stages || []).forEach((s) => {
      const name = s.stageName;
      if (!name) return;

      const stageFeedbackPresent =
        (typeof s.candidateStageFeedback === "string" &&
          s.candidateStageFeedback.trim() !== "") ||
        s.candidateStageFeedback === true;

      const feedbackAdded = !!s.feedbackAdded;

      feedbackMap[name] = {
        stageFeedbackPresent,
        feedbackAdded,
      };
    });

    const isStageCompleted = (stageName) => {
      const f = feedbackMap[stageName];
      if (!f) return false;
      if (f.feedbackAdded) return true;
      return f.stageFeedbackPresent;
    };

    const activeStage = interview.stages.find(
      (stage) => !isStageCompleted(stage.stageName),
    );

    return activeStage || interview.stages[interview.stages.length - 1] || null;
  };

  const canStartNextStage = (interview, stageIndex) => {
    if (!interview.stages || stageIndex === 0) return true;

    for (let i = 0; i < stageIndex; i++) {
      const previousStage = interview.stages[i];
      const hasCandidateFeedback =
        previousStage.candidateStageFeedback &&
        previousStage.candidateStageFeedback.trim().length > 0;
      if (!hasCandidateFeedback) {
        return false;
      }
    }
    return true;
  };

  const isStageCompleted = (stage) => {
    return (
      stage.candidateStageFeedback &&
      stage.candidateStageFeedback.trim().length > 0
    );
  };

  const canMarkStageComplete = (interview, stageIndex) => {
    if (!interview.stages) return false;
    if (stageIndex === 0) return true;
    return canStartNextStage(interview, stageIndex);
  };

  const getNextStageRequiringFeedback = (interview) => {
    if (!interview.stages) return null;

    for (let i = 0; i < interview.stages.length; i++) {
      const stage = interview.stages[i];
      const hasFeedback =
        stage.candidateStageFeedback &&
        stage.candidateStageFeedback.trim().length > 0;
      if (!hasFeedback) {
        return { stage, index: i };
      }
    }
    return null;
  };

  const today = new Date();

  const handleMarkStageComplete = (interview, stage) => {
    if (
      interview.status !== "in_progress" &&
      interview.status !== "scheduled"
    ) {
      toast.error(
        "Interview must be in progress or scheduled to complete stages",
      );
      return;
    }

    const stageIndex = interview.stages.findIndex(
      (s) => s.stageName === stage.stageName,
    );
    if (!canMarkStageComplete(interview, stageIndex)) {
      const previousStage = interview.stages[stageIndex - 1];
      toast.error(
        `Please complete and add feedback for the previous stage "${previousStage.stageName}" first.`,
      );
      return;
    }

    if (isStageCompleted(stage)) {
      toast.success("This stage is already completed with feedback.");
      return;
    }

    setSelectedInterviewForSchedule(interview);
    setSelectedStageForComplete(stage);
    setStageCompleteDialogOpen(true);
  };

  const handleCloseStageCompleteDialog = () => {
    setStageCompleteDialogOpen(false);
    setSelectedStageForComplete(null);
  };

  const handleConfirmStageComplete = () => {
    if (!selectedStageForComplete || !selectedInterviewForSchedule) return;
    handleCloseStageCompleteDialog();
    handleOpenFeedbackDialog(
      selectedInterviewForSchedule,
      selectedStageForComplete,
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
      case "accepted":
      case "slot_selected":
        return {
          backgroundColor: "#9b42e9",
          color: "#FFFFFF",
        };
      case "slot_rejected":
      case "declined":
      case "cancelled":
      case "admin_declined":
        return {
          backgroundColor: "#ba68c8",
          color: "#FFFFFF",
        };
      case "pending":
      case "requested":
        return {
          backgroundColor: "#8c5eb4ff",
          color: "#FFFFFF",
        };
      case "scheduled":
      case "slot_proposed":
      case "slots_proposed":
      case "in_progress":
      default:
        return {
          backgroundColor: "#8c5eb4ff",
          color: "#FFFFFF",
        };
    }
  };

  const getDecisionColor = (decision) => {
    if (!decision) {
      return {
        backgroundColor: "#8c5eb4ff",
        color: "#FFFFFF",
      };
    }
    switch (decision?.toLowerCase()) {
      case "selected":
        return {
          backgroundColor: "#9b42e9",
          color: "#FFFFFF",
        };
      case "rejected":
        return {
          backgroundColor: "#ba68c8",
          color: "#FFFFFF",
        };
      case "on_hold":
        return {
          backgroundColor: "#8c5eb4ff",
          color: "#FFFFFF",
        };
      default:
        return {
          backgroundColor: "#8c5eb4ff",
          color: "#FFFFFF",
        };
    }
  };

  const formatDecision = (decision) => {
    if (!decision) return "Pending";
    return (
      decision.charAt(0).toUpperCase() + decision.slice(1).replace(/_/g, " ")
    );
  };

  const handleViewDetails = (interview) => {
    setSelectedInterview(interview);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInterview(null);
  };

  const handleOpenDecisionDialog = (interview) => {
    setSelectedInterviewForDecision(interview);
    setDecisionValue(interview.overallResult || "");
    setDecisionDialogOpen(true);
  };

  const handleCloseDecisionDialog = () => {
    setDecisionDialogOpen(false);
    setSelectedInterviewForDecision(null);
    setDecisionValue("");
  };

  const fetchIndividualInterview = async (interviewId) => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `/api/job-postings/interviews/${interviewId}`,
      );

      const updatedInterview = res.data;

      updatedInterview.stages = (updatedInterview.stages || []).map((s) => {
        const isSelected =
          s.candidateAction === "selected" || s.candidateAction === "accepted";
        // For slot_rejected status, don't lock rejected stages to allow re-proposal
        const isRejectedStage =
          updatedInterview.status === "slot_rejected" &&
          s.candidateAction === "rejected";
        const isLocked = (s.locked === true || isSelected) && !isRejectedStage;

        // Helper to extract time from ISO string
        const getTimeFromISO = (isoString) => {
          if (!isoString) return null;
          const date = new Date(isoString);
          let hours = date.getHours();
          const minutes = date.getMinutes();
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12;
          hours = hours ? hours : 12; // the hour '0' should be '12'
          const minutesStr = minutes < 10 ? "0" + minutes : minutes;
          return `${hours}:${minutesStr} ${ampm}`;
        };

        // Helper to extract date string (YYYY-MM-DD) from ISO string
        const getDateFromISO = (isoString) => {
          if (!isoString) return null;
          return isoString.split("T")[0];
        };

        return {
          ...s,
          // If candidate selected → use selected slot
          date: isSelected
            ? s.candidateSelectedSlot?.day ||
            s.candidateSelectedSlot?.date ||
            getDateFromISO(s.candidateSelectedSlot?.start)
            : s.date || null,

          time: isSelected
            ? s.candidateSelectedSlot?.time ||
            getTimeFromISO(s.candidateSelectedSlot?.start)
            : s.time || null,

          // UI control flags
          locked: isLocked, // disable editing
          allowPropose: !isLocked, // allow proposing only if not locked
        };
      });

      // update the interviews list
      setInterviews((prev) =>
        prev.map((item) =>
          item.interviewId === interviewId ? updatedInterview : item,
        ),
      );

      // if the details dialog is open for this interview, update it
      setSelectedInterview((prev) =>
        prev && prev.interviewId === interviewId ? updatedInterview : prev,
      );

      // also update other interview-specific states that may be open (schedule / decision)
      setSelectedInterviewForSchedule((prev) =>
        prev && prev.interviewId === interviewId ? updatedInterview : prev,
      );

      setSelectedInterviewForDecision((prev) =>
        prev && prev.interviewId === interviewId ? updatedInterview : prev,
      );
      setSelectedInterviewForAction((prev) =>
        prev && prev.interviewId === interviewId ? updatedInterview : prev,
      );

      // Return the updated interview for immediate use by callers
      return updatedInterview;
    } catch (error) {
      console.error("Error fetching individual interview:", error);
      toast.error(error.message || "Failed to fetch interview details");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDecision = async () => {
    if (!selectedInterviewForDecision || !decisionValue) return;

    try {
      const response = await fetch(
        `${API_BASE}/api/job-postings/interviews/${selectedInterviewForDecision.interviewId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "set_final_result",
            data: {
              overallResult: decisionValue,
              overallFeedback: {
                // Preserve existing feedback if we are just setting decision
                feedback:
                  selectedInterviewForDecision.overallFeedback?.feedback || "",
                candidateFeedback:
                  selectedInterviewForDecision.overallFeedback
                    ?.candidateFeedback || "",
              },
            },
          }),
        },
      );

      if (response.ok) {
        handleCloseDecisionDialog();
        await fetchIndividualInterview(
          selectedInterviewForDecision.interviewId,
        );
      }
    } catch (error) {
      console.error("Error updating interview decision:", error);
    }
  };

  const formatTimeForInput = (time12h) => {
    if (!time12h) return "";
    const match = time12h.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return time12h;
    let hour = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && hour !== 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return `${String(hour).padStart(2, "0")}:${minutes}`;
  };

  const handleOpenScheduleDialog = (interview) => {
    if (
      interview.status !== "accepted" &&
      interview.status !== "scheduled" &&
      interview.status !== "in_progress"
    ) {
      toast.error("Interview must be accepted by candidate before scheduling");
      return;
    }
    setSelectedInterviewForSchedule(interview);

    // Initialize scheduleDatesByStage for each stage
    const initialScheduleDates = {};

    if (
      interview.stages &&
      interview.stages.length > 0 &&
      interview.status !== "in_progress"
    ) {
      setStages(
        interview.stages.map((s, index) => {
          // Helper to convert 12h time (e.g. "5:54 PM") to 24h (e.g. "17:54")
          let time24 = "";
          if (s.time) {
            const [timePart, modifier] = s.time.split(" ");
            if (modifier) {
              let [hours, minutes] = timePart.split(":");
              if (hours === "12") {
                hours = "00";
              }
              if (modifier === "PM") {
                hours = parseInt(hours, 10) + 12;
              }
              time24 = `${hours}:${minutes}`;
            } else {
              time24 = s.time; // Already 24h or invalid
            }
          }

          // Initialize with one date/time slot for each stage
          initialScheduleDates[index] = [
            {
              date: null,
              timeSlots: [""],
            },
          ];

          return {
            ...s,
            date: s.date, // Keep as string or Date, DatePicker handles it if passed to value prop as Date
            time: time24,
            stageFeedback: s.stageFeedback || null,
            candidateStageFeedback: s.candidateStageFeedback || "",
            locked: s.locked || s.candidateAction === "selected",
            isProposed: s.isProposed !== undefined ? s.isProposed : true,
            interviewers:
              s.interviewers && s.interviewers.length > 0
                ? s.interviewers
                : s.interviewer
                  ? [s.interviewer]
                  : [""],
            isNewStage: false,
          };
        }),
      );
    } else {
      setStages([
        {
          stageName: "",
          date: "",
          time: "",
          interviewers: [""],
          stageFeedback: null,
          candidateStageFeedback: "",
          isProposed: true,
          isNewStage: true,
        },
      ]);

      // Initialize with one date/time slot for the first stage
      initialScheduleDates[0] = [
        {
          date: null,
          timeSlots: [""],
        },
      ];
    }

    setScheduleDatesByStage(initialScheduleDates);
    setScheduleDialogOpen(true);
  };

  const handleCloseScheduleDialog = () => {
    setScheduleDialogOpen(false);
    setSelectedInterviewForSchedule(null);
    setStages([
      {
        stageName: "",
        date: "",
        time: "",
        interviewers: [""],
        stageFeedback: null,
        candidateStageFeedback: "",
        isNewStage: true,
      },
    ]);
    setScheduleDatesByStage({});
  };

  const handleAddStage = () => {
    const newStageIndex = stages.length;
    setStages([
      ...stages,
      {
        stageName: "",
        date: "",
        time: "",
        interviewers: [""],
        stageFeedback: null,
        candidateStageFeedback: "",
        isProposed: true,
        isNewStage: true,
      },
    ]);

    // Initialize scheduleDatesByStage for the new stage
    setScheduleDatesByStage({
      ...scheduleDatesByStage,
      [newStageIndex]: [
        {
          date: null,
          timeSlots: [""],
        },
      ],
    });
  };

  // console.log("interviews", interviews);
  const handleRemoveStage = (index) => {
    setStages(stages.filter((_, i) => i !== index));
  };

  const handleStageChange = (index, field, value, subIndex = null) => {
    // Update the value first
    const updatedStages = [...stages];

    // If updating an existing stage (not new), prevent changing name or interviewers
    if (
      !updatedStages[index].isNewStage &&
      (field === "stageName" || field === "interviewers")
    ) {
      return;
    }

    if (field === "interviewers") {
      const updatedInterviewers = [...updatedStages[index].interviewers];
      if (subIndex !== null) {
        updatedInterviewers[subIndex] = value;
      }
      updatedStages[index] = {
        ...updatedStages[index],
        interviewers: updatedInterviewers,
      };
    } else {
      updatedStages[index] = { ...updatedStages[index], [field]: value };
    }
    setStages(updatedStages);

    // Check for duplicate stage names in the schedule dialog after update
    if (field === "stageName" && value.trim() !== "") {
      const isDuplicate = updatedStages.some(
        (stage, idx) =>
          idx !== index &&
          stage.stageName?.trim().toLowerCase() === value.trim().toLowerCase(),
      );

      if (isDuplicate) {
        toast.warning(
          "Stage name already exists. Please use a different name.",
        );
      }
    }
  };

  const handleAddInterviewer = (stageIndex) => {
    const updatedStages = [...stages];
    updatedStages[stageIndex].interviewers.push("");
    setStages(updatedStages);
  };

  const handleRemoveInterviewer = (stageIndex, interviewerIndex) => {
    const updatedStages = [...stages];
    updatedStages[stageIndex].interviewers = updatedStages[
      stageIndex
    ].interviewers.filter((_, i) => i !== interviewerIndex);
    setStages(updatedStages);
  };

  const formatTimeForAPI = (time24h) => {
    if (!time24h) return "";
    const [hours, minutes] = time24h.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const dateObj = new Date(dateStr);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSaveSchedule = async () => {
    if (!selectedInterviewForSchedule) return;

    // Validate that each stage has name and interviewer
    const invalidStages = stages.filter(
      (stage) =>
        !stage.locked &&
        (!stage.stageName?.trim() ||
          !stage.interviewers ||
          stage.interviewers.filter((i) => i.trim() !== "").length === 0),
    );

    if (invalidStages.length > 0) {
      toast.error(
        "Please provide stage name and at least one interviewer for all stages",
      );
      return;
    }

    // Check for duplicate stage names before saving
    const stageNames = stages.map((s) => s.stageName?.trim().toLowerCase());
    const hasDuplicates = stageNames.some(
      (name, idx) => stageNames.indexOf(name) !== idx,
    );

    if (hasDuplicates) {
      toast.error(
        "Duplicate stage names found. Please ensure all stage names are unique.",
      );
      return;
    }

    if (selectedInterviewForSchedule.status === "in_progress") {
      const existingNames = (selectedInterviewForSchedule.stages || []).map(
        (s) => s.stageName?.trim().toLowerCase(),
      );
      const hasExistingDuplicate = stageNames.some((name) =>
        existingNames.includes(name),
      );
      if (hasExistingDuplicate) {
        toast.error(
          "One or more stage names already exist in the schedule. Please use unique names.",
        );
        return;
      }
    }

    // Validate that each stage has at least one date with a time slot and no duplicates
    let hasValidSlots = false;
    for (let i = 0; i < stages.length; i++) {
      // Skip locked/completed stages — only validate stages being edited
      if (stages[i].locked) continue;
      const rawStageDates = scheduleDatesByStage[i];
      if (!rawStageDates) continue;

      // Strip trailing empty "active input" placeholder entries before validation
      // (the chip UI always keeps one empty entry at the end for new input)
      const cleanedDates = rawStageDates
        .map((dateObj) => {
          const cleanedSlots = [...dateObj.timeSlots];
          while (
            cleanedSlots.length > 0 &&
            (!cleanedSlots[cleanedSlots.length - 1] ||
              cleanedSlots[cleanedSlots.length - 1].trim() === "")
          ) {
            cleanedSlots.pop();
          }
          return { ...dateObj, timeSlots: cleanedSlots };
        })
        // Only keep dateObjs that have BOTH a date AND at least one filled time slot
        .filter(
          (dateObj) =>
            dateObj.date && dateObj.timeSlots.some((s) => s && s.trim() !== ""),
        );

      if (cleanedDates.length > 0) {
        hasValidSlots = true;
      }

      // Check for duplicate time slots across all entries for the same date
      const dateSlotPairs = [];
      for (const dateObj of cleanedDates) {
        const dateStr = formatDateForInput(dateObj.date);
        for (const slot of dateObj.timeSlots) {
          if (slot && slot.trim()) {
            dateSlotPairs.push(`${dateStr}-${slot.trim()}`);
          }
        }
      }
      const uniqueDateSlotPairs = new Set(dateSlotPairs);
      if (uniqueDateSlotPairs.size !== dateSlotPairs.length) {
        toast.error(
          `Duplicate time slots found in stage ${i + 1}. Please select different times for the same date.`,
        );
        return;
      }
    }

    if (!hasValidSlots) {
      toast.error(
        "Please add at least one date with a time slot for at least one stage",
      );
      return;
    }

    try {
      // Update the stages array with proposedSlots per stage
      const formattedStages = stages.map((stage, stageIndex) => {
        if (stage.locked || stage.candidateAction === "selected") {
          return stage;
        }

        const rawDates = scheduleDatesByStage[stageIndex] || [];

        // Clean: strip trailing empty slots, keep only valid date+time entries
        const validDates = rawDates
          .map((dateObj) => {
            const cleanedSlots = [...dateObj.timeSlots];
            while (
              cleanedSlots.length > 0 &&
              (!cleanedSlots[cleanedSlots.length - 1] ||
                cleanedSlots[cleanedSlots.length - 1].trim() === "")
            ) {
              cleanedSlots.pop();
            }
            return { ...dateObj, timeSlots: cleanedSlots };
          })
          .filter(
            (dateObj) =>
              dateObj.date &&
              dateObj.timeSlots.some((s) => s && s.trim() !== ""),
          );

        // Generate unique slot IDs and format the proposed slots for this stage
        // Group time slots by date to avoid duplicate date blocks in the payload
        const datesMap = new Map();
        validDates.forEach((dateObj) => {
          const formattedDate = formatDateForInput(dateObj.date);
          if (!datesMap.has(formattedDate)) datesMap.set(formattedDate, []);

          dateObj.timeSlots
            .filter((slot) => slot.trim() !== "")
            .forEach((slot) => {
              datesMap.get(formattedDate).push(slot);
            });
        });

        // Generate unique slot IDs and format the proposed slots for this stage
        const proposedSlots = Array.from(datesMap.entries()).map(
          ([formattedDate, timeSlots]) => {
            const slots = timeSlots.map((slot, index) => ({
              slotId: `${stage.stageName
                .replace(/\s+/g, "-")
                .toLowerCase()}-${formattedDate}-${index + 1}`,
              time: formatTimeForAPI(slot),
            }));

            return {
              date: formattedDate,
              slots: slots,
            };
          },
        );

        return {
          ...stage,
          stageName: stage.stageName,
          interviewer: stage.interviewer,
          interviewers: stage.interviewers
            ? stage.interviewers.filter((iv) => iv && iv.trim() !== "")
            : [],
          proposedSlots: proposedSlots,
          date: null,
          time: null,
          stageFeedback: stage.stageFeedback || null,
          candidateStageFeedback: stage.candidateStageFeedback || "",
        };
      });

      const currentStatus = selectedInterviewForSchedule.status;
      let newStatus = "slot_proposed";

      // If adding new rounds to in_progress interview, merge with existing stages
      if (currentStatus === "in_progress") {
        const existingStages = selectedInterviewForSchedule.stages || [];
        const existingStageNames = new Set(
          existingStages.map((s) => s.stageName),
        );
        const newStages = formattedStages.filter(
          (s) => !existingStageNames.has(s.stageName),
        );
        const mergedStages = [...existingStages, ...newStages];

        const response = await fetch(
          `${API_BASE}/api/job-postings/interviews/${selectedInterviewForSchedule.interviewId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              action: newStages.length > 0 ? "create_stage" : newStatus,
              data: {
                proposedSlots:
                  newStages.length > 0
                    ? newStages[0].proposedSlots
                    : mergedStages[0].proposedSlots,
                stageName:
                  newStages.length > 0
                    ? newStages[0].stageName
                    : mergedStages[0].stageName,
                interviewers:
                  newStages.length > 0
                    ? newStages[0].interviewers.filter((iv) => iv.trim() !== "")
                    : mergedStages[0].interviewers.filter(
                      (iv) => iv.trim() !== "",
                    ),
              },
            }),
          },
        );

        if (response.ok) {
          toast.success("New stage added successfully");
          handleCloseScheduleDialog();
          await fetchIndividualInterview(
            selectedInterviewForSchedule.interviewId,
          );
        } else {
          toast.error("Failed to add new stage");
        }
      } else {
        // For accepted or scheduled status, replace all stages
        const response = await fetch(
          `${API_BASE}/api/job-postings/interviews/${selectedInterviewForSchedule.interviewId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              status: newStatus,
              stages: formattedStages,
            }),
          },
        );

        if (response.ok) {
          toast.success("Time slots proposed successfully");
          handleCloseScheduleDialog();
          await fetchIndividualInterview(
            selectedInterviewForSchedule.interviewId,
          );
        } else {
          toast.error("Failed to propose time slots");
        }
      }
    } catch (error) {
      console.error("Error scheduling interview:", error);
      toast.error("Error scheduling interview");
    }
  };

  const handleStartStage = async (interview) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/job-postings/interviews/${interview.interviewId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "start_stage",
            data: {},
          }),
        },
      );

      if (response.ok) {
        toast.success("Interview stage started");
        await fetchIndividualInterview(interview.interviewId);
      } else {
        toast.error("Failed to start interview stage");
      }
    } catch (error) {
      console.error("Error starting stage:", error);
      toast.error("Error starting stage");
    }
  };

  const handleCancelInterview = async (interview) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/job-postings/interviews/${interview.interviewId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: "cancel_interview",
          }),
        },
      );

      if (response.ok) {
        toast.success("Interview cancelled");
        await fetchIndividualInterview(interview.interviewId);
      } else {
        toast.error("Failed to cancel interview");
      }
    } catch (error) {
      console.error("Error cancelling interview:", error);
      toast.error("Error cancelling interview");
    }
  };

  const handleOpenFeedbackDialog = (interview, stage) => {
    if (
      interview.status !== "in_progress" &&
      interview.status !== "scheduled"
    ) {
      toast.error("Interview must be in progress or scheduled to add feedback");
      return;
    }

    const stageIndex = interview.stages.findIndex(
      (s) => s.stageName === stage.stageName,
    );
    if (stageIndex > 0 && !canStartNextStage(interview, stageIndex)) {
      const previousStage = interview.stages[stageIndex - 1];
      toast.error(
        `Please add feedback for the previous stage "${previousStage.stageName}" before adding feedback for this stage.`,
      );
      return;
    }

    setSelectedInterviewForSchedule(interview);
    setSelectedStageForFeedback(stage);
    setStageFeedback(stage.stageFeedback || "");
    setCandidateStageFeedback(stage.candidateStageFeedback || "");

    setFeedbackDialogOpen(true);
  };

  const handleCloseFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
    setSelectedInterviewForSchedule(null);
    setSelectedStageForFeedback(null);
    setStageFeedback("");
  };

  const handleSaveFeedback = () => {
    if (!selectedInterviewForSchedule || !selectedStageForFeedback) return;

    if (!candidateStageFeedback.trim()) {
      toast.error("Please enter candidate feedback before saving");
      return;
    }

    const saveLogic = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/job-postings/interviews/${selectedInterviewForSchedule.interviewId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              action: "add_feedback",
              data: {
                stageFeedback: stageFeedback,
                candidateStageFeedback: candidateStageFeedback,
                corporate: stageFeedback,
                candidate: candidateStageFeedback,
              },
            }),
          },
        );

        if (response.ok) {
          handleCloseFeedbackDialog();
          // await fetchInterviews();
          const updatedInterview = await fetchIndividualInterview(
            selectedInterviewForSchedule.interviewId,
          );

          // Use the fresh data returned from fetchIndividualInterview
          if (updatedInterview) {
            const stageIndex = updatedInterview.stages.findIndex(
              (s) => s.stageName === selectedStageForFeedback.stageName,
            );
            const isLastStage =
              stageIndex === updatedInterview.stages.length - 1;

            if (isLastStage) {
              setTimeout(() => {
                openConfirm({
                  title: "All stages completed",
                  message:
                    "All stages are completed. Would you like to mark the interview as completed and add overall feedback?",
                  confirmLabel: "Yes, complete interview",
                  cancelLabel: "No",
                  onConfirm: () => {
                    handleCompleteInterviewWithFeedback(updatedInterview);
                    closeConfirm();
                  },
                });
              }, 500);
            }
          }
        }
        closeConfirm();
      } catch (error) {
        console.error("Error saving feedback:", error);
        closeConfirm();
      }
    };

    saveLogic();
  };

  const handleCompleteInterviewWithFeedback = (interview) => {
    // interview parameter now contains fresh data from fetchIndividualInterview
    setSelectedInterviewForSchedule(interview);
    setOverallFeedback(interview.overallFeedback?.feedback || "");
    setOverallCandidateFeedback(
      interview.overallFeedback?.candidateFeedback || "",
    );
    setOverallDecision(interview.overallResult || "");
    setOverallFeedbackDialogOpen(true);
  };

  const handleCloseOverallFeedbackDialog = () => {
    setOverallFeedbackDialogOpen(false);
    setSelectedInterviewForSchedule(null);
    setOverallFeedback("");
    setOverallCandidateFeedback("");
    setOverallDecision("");
  };

  const handleSaveOverallFeedback = () => {
    if (!selectedInterviewForSchedule) return;

    if (!overallCandidateFeedback.trim()) {
      toast.error("Please enter overall candidate feedback before saving");
      return;
    }

    const saveLogic = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/job-postings/interviews/${selectedInterviewForSchedule.interviewId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              action: "set_final_result",
              data: {
                overallResult:
                  overallDecision ||
                  selectedInterviewForSchedule.overallResult ||
                  null,
                overallFeedback: {
                  feedback: overallFeedback,
                  candidateFeedback: overallCandidateFeedback,
                },
              },
            }),
          },
        );

        if (response.ok) {
          handleCloseOverallFeedbackDialog();
          // await fetchInterviews();
          await fetchIndividualInterview(
            selectedInterviewForSchedule.interviewId,
          );
        }
        closeConfirm();
      } catch (error) {
        console.error("Error saving overall feedback:", error);
        closeConfirm();
      }
    };

    saveLogic();
  };

  // Propose Time Slots Modal Handlers
  const handleOpenProposeSlotDialog = (interview) => {
    if (
      interview.status !== "accepted" &&
      interview.status !== "scheduled" &&
      interview.status !== "in_progress" &&
      interview.status !== "slot_rejected" &&
      interview.status !== "slots_proposed" &&
      interview.status !== "propose_new_slots"
    ) {
      toast.error(
        "Interview must be accepted by candidate before proposing slots",
      );
      return;
    }

    // Calculate active stage index
    // Active stage is the first stage that is NOT completed
    let activeIndex = 0;
    if (interview.stages && interview.stages.length > 0) {
      const foundIndex = interview.stages.findIndex((stage) => {
        // If status is present, use it. Otherwise fallback to feedback check (legacy)
        if (stage.status) {
          return stage.status !== "completed";
        }

        const isCompleted =
          stage.stageFeedback && stage.stageFeedback.trim().length > 0;
        return !isCompleted;
      });
      activeIndex = foundIndex !== -1 ? foundIndex : interview.stages.length;
    }

    // If the interview status is 'slot_rejected', we might need to find the rejected stage
    // Although the above logic should catch it if status is 'pending' or 'slots_proposed' etc.
    // But if the backend sets the *interview* status to 'slot_rejected', the stage status might be 'pending' (re-propose)
    // The user request says: "stages[1].status = 'pending'" after rejection.
    // So the above logic (find first non-completed) should work fine as 'pending' !== 'completed'.
    // We can keep this check just in case or remove it if redundant.
    // I'll keep it but ensure it doesn't override if foundIndex is valid.
    if (
      interview.status === "slot_rejected" &&
      activeIndex === interview.stages.length
    ) {
      // If we didn't find an active stage (all completed?), but status is rejected?
      // That shouldn't happen.
      const rejectedIndex = interview.stages.findIndex(
        (s) => s.candidateAction === "rejected",
      );
      if (rejectedIndex !== -1) {
        activeIndex = rejectedIndex;
      }
    }

    // Inject isActive and isCompleted flag into stages
    const stagesWithActiveFlag = (interview.stages || []).map(
      (stage, index) => {
        const isCompleted =
          stage.stageFeedback && stage.stageFeedback.trim().length > 0;

        return {
          ...stage,
          isActive: index === activeIndex,
          isCompleted: isCompleted,
        };
      },
    );

    const interviewWithActiveFlag = {
      ...interview,
      stages: stagesWithActiveFlag,
    };

    setSelectedInterviewForProposeSlot(interviewWithActiveFlag);

    // Initialize proposedDatesByStage
    const initialProposedDates = {};
    if (interviewWithActiveFlag.stages) {
      interviewWithActiveFlag.stages.forEach((stage, index) => {
        // If we have existing proposed slots, populate them
        if (stage.proposedSlots && stage.proposedSlots.length > 0) {
          // Group by date
          const datesMap = {};
          stage.proposedSlots.forEach((slot) => {
            // slot.day or slot.date might be used? User example says "date" in the block, but "day" in my code?
            // My code used "day" in the payload construction: "day: formattedDate".
            // The user example says: "date": "2026-01-20".
            // I should check what I'm sending. I'm sending "date" in the block.
            // But wait, the previous code used "day".
            // I should probably support both or check what's returned.
            // The user request says: "stages[0].proposedSlots set to provided array".
            // So it will come back as I sent it.
            const dateKey = slot.date || slot.day;

            // If slot has 'start' (ISO), extract time. If 'time' (legacy), use it.
            // The structure is: { date: "...", slots: [ { start: "...", ... } ] }
            // Wait, the structure I'm sending is: [ { date: "...", slots: [ ... ] } ]
            // But `stage.proposedSlots` in `handleOpenProposeSlotDialog` seems to assume a flat list of slots?
            // Or does it assume the structure?
            // The previous code:
            // stage.proposedSlots.forEach((slot) => {
            //   if (!datesMap[slot.day]) ...
            //   datesMap[slot.day].push(formatTimeForInput(slot.time));
            // });
            // This implies `stage.proposedSlots` was a flat array of slots with `day` property?
            // But my `handleSaveProposedSlots` constructs:
            // proposedSlots = [ { date: "...", slots: [ ... ] } ]
            // So `stage.proposedSlots` will be an array of DAY BLOCKS.
            // So I need to iterate differently!

            // If the backend returns exactly what I sent:
            // stage.proposedSlots is [ { date: "...", slots: [ { start: "...", ... } ] }, ... ]

            // I need to handle this structure.

            // Check if it's the new structure (has 'slots' array inside)
            if (slot.slots && Array.isArray(slot.slots)) {
              const date = slot.date || slot.day;
              if (!datesMap[date]) datesMap[date] = [];
              slot.slots.forEach((s) => {
                let timeStr = "";
                if (s.start) {
                  const d = new Date(s.start);
                  const hours = d.getHours().toString().padStart(2, "0");
                  const minutes = d.getMinutes().toString().padStart(2, "0");
                  timeStr = `${hours}:${minutes}`; // 24h format for input
                } else if (s.time) {
                  timeStr = formatTimeForInput(s.time); // This expects 12h? formatTimeForInput converts 12h to 24h?
                  // formatTimeForInput implementation:
                  // if match 12h, convert to 24h. If not match, return as is.
                  // So it handles "10:00 AM" -> "10:00".
                }
                if (timeStr) datesMap[date].push(timeStr);
              });
            } else {
              // Legacy flat structure or single slot?
              // Assuming legacy flat structure had day and time?
              const date = slot.day || slot.date;
              if (date) {
                if (!datesMap[date]) datesMap[date] = [];
                datesMap[date].push(formatTimeForInput(slot.time));
              }
            }
          });

          initialProposedDates[index] = Object.keys(datesMap).map((date) => ({
            date: new Date(date),
            timeSlots: datesMap[date],
          }));

          // Append a completely empty block to act as the clean active input
          initialProposedDates[index].push({
            date: null,
            timeSlots: [""],
          });
        } else {
          // Default empty slot for this stage
          initialProposedDates[index] = [
            {
              date: null,
              timeSlots: [""],
            },
          ];
        }
      });
    }

    // If activeIndex points to a new stage (length of stages), initialize it
    if (!initialProposedDates[activeIndex]) {
      initialProposedDates[activeIndex] = [
        {
          date: null,
          timeSlots: [""],
        },
      ];
    }

    setProposedDatesByStage(initialProposedDates);
    setProposeSlotDialogOpen(true);
  };

  const handleCloseProposeSlotDialog = () => {
    setProposeSlotDialogOpen(false);
    setSelectedInterviewForProposeSlot(null);
    setProposedDatesByStage({});
  };

  const handleAddDate = (stageIndex) => {
    const stageDates = [...(proposedDatesByStage[stageIndex] || [])];
    const lastDateObj = stageDates[stageDates.length - 1];

    // Validate current active input before adding another date
    if (lastDateObj) {
      const lastSlot = lastDateObj.timeSlots[lastDateObj.timeSlots.length - 1];

      // If both date and time are filled, check for duplicates before committing
      if (lastDateObj.date && lastSlot && lastSlot.trim() !== "") {
        const currentDateStr = formatDateForInput(lastDateObj.date);
        // Check for duplicate time slot across all dates in this stage
        const otherSlots = lastDateObj.timeSlots.slice(0, -1);
        if (otherSlots.includes(lastSlot)) {
          toast.error(
            "This time slot already exists for this date. Please select a different time.",
          );
          return;
        }
        for (let i = 0; i < stageDates.length - 1; i++) {
          if (
            stageDates[i].date &&
            formatDateForInput(stageDates[i].date) === currentDateStr
          ) {
            if (stageDates[i].timeSlots.includes(lastSlot)) {
              toast.error(
                "This time slot already exists for this date. Please select a different time.",
              );
              return;
            }
          }
        }
      }
    }

    setProposedDatesByStage({
      ...proposedDatesByStage,
      [stageIndex]: [
        ...(proposedDatesByStage[stageIndex] || []),
        {
          date: null,
          timeSlots: [""],
        },
      ],
    });
  };

  const handleRemoveDate = (stageIndex, dateIndex) => {
    const stageDates = proposedDatesByStage[stageIndex] || [];
    setProposedDatesByStage({
      ...proposedDatesByStage,
      [stageIndex]: stageDates.filter((_, index) => index !== dateIndex),
    });
  };

  const handleAddTimeSlot = (stageIndex, dateIndex) => {
    const stageDates = [...(proposedDatesByStage[stageIndex] || [])];
    const currentSlots = stageDates[dateIndex].timeSlots;
    const lastSlot = currentSlots[currentSlots.length - 1];

    // Don't add if current slot is empty
    if (!lastSlot || lastSlot.trim() === "") {
      toast.error(
        "Please fill in the current time slot before adding another.",
      );
      return;
    }

    // Don't add if date is not selected
    if (!stageDates[dateIndex].date) {
      toast.error("Please select a date before adding another time slot.");
      return;
    }

    // Check for duplicate time slot within the same date
    const otherSlots = currentSlots.slice(0, -1);
    if (otherSlots.includes(lastSlot)) {
      toast.error(
        "This time slot already exists for this date. Please select a different time.",
      );
      return;
    }

    // Check for duplicate time slot across all dates in this stage
    const currentDateStr = formatDateForInput(stageDates[dateIndex].date);
    for (let i = 0; i < stageDates.length; i++) {
      if (i === dateIndex) continue;
      if (
        stageDates[i].date &&
        formatDateForInput(stageDates[i].date) === currentDateStr
      ) {
        if (stageDates[i].timeSlots.includes(lastSlot)) {
          toast.error(
            "This time slot already exists for this date in another entry. Please select a different time.",
          );
          return;
        }
      }
    }

    stageDates[dateIndex].timeSlots.push("");
    setProposedDatesByStage({
      ...proposedDatesByStage,
      [stageIndex]: stageDates,
    });
  };

  const handleRemoveTimeSlot = (stageIndex, dateIndex, slotIndex) => {
    const stageDates = [...(proposedDatesByStage[stageIndex] || [])];
    stageDates[dateIndex].timeSlots = stageDates[dateIndex].timeSlots.filter(
      (_, index) => index !== slotIndex,
    );
    setProposedDatesByStage({
      ...proposedDatesByStage,
      [stageIndex]: stageDates,
    });
  };

  const handleDateChange = (stageIndex, dateIndex, newDate) => {
    const stageDates = [...(proposedDatesByStage[stageIndex] || [])];
    const targetDateObj = stageDates[dateIndex];

    // Check for duplicate dates in the same stage
    if (newDate) {
      const newDateStr = formatDateForInput(newDate);
      const isDuplicate = stageDates.some(
        (dateObj, idx) =>
          idx !== dateIndex &&
          dateObj.date &&
          formatDateForInput(dateObj.date) === newDateStr,
      );

      if (isDuplicate) {
        toast.warning(
          "This date has already been added for this stage. Please select a different date.",
        );
      }
    }

    // If the active dateObj has committed chips (timeSlots > 1), we shouldn't mutate
    // the date for the existing chips. Instead, we detach the active input into a new dateObj.
    if (targetDateObj.timeSlots.length > 1) {
      // 1. Remove the active slot from the current dateObj
      const activeSlot = targetDateObj.timeSlots.pop();

      // 2. Append a new dateObj with the newDate and the activeSlot
      stageDates.push({
        date: newDate,
        timeSlots: [activeSlot],
      });
    } else {
      // It's just the active input, safe to mutate the date directly
      targetDateObj.date = newDate;
    }

    setProposedDatesByStage({
      ...proposedDatesByStage,
      [stageIndex]: stageDates,
    });
  };

  const handleTimeSlotChange = (stageIndex, dateIndex, slotIndex, value) => {
    const stageDates = [...(proposedDatesByStage[stageIndex] || [])];

    // Check for duplicate time slots within the same date and stage
    if (value && stageDates[dateIndex]?.timeSlots) {
      const isDuplicateInSameDate = stageDates[dateIndex].timeSlots.some(
        (slot, idx) => idx !== slotIndex && slot === value,
      );

      if (isDuplicateInSameDate) {
        toast.error(
          "This time slot has already been added for this date. Please select a different time.",
        );
        return;
      }
    }

    // Check for duplicate time slots across different stages on the same date
    if (value && stageDates[dateIndex]?.date) {
      const currentDate = formatDateForInput(stageDates[dateIndex].date);

      const isDuplicateTimeAcrossStages = Object.keys(
        proposedDatesByStage,
      ).some((stgIdx) => {
        if (parseInt(stgIdx) === stageIndex) return false; // Skip current stage
        const otherStageDates = proposedDatesByStage[stgIdx] || [];

        return otherStageDates.some((dateObj) => {
          if (
            !dateObj.date ||
            formatDateForInput(dateObj.date) !== currentDate
          ) {
            return false; // Different date, no conflict
          }

          // Same date, check if any time slot matches
          return dateObj.timeSlots.some((slot) => slot === value);
        });
      });

      if (isDuplicateTimeAcrossStages) {
        toast.error(
          "This time slot has already been selected for another stage on the same date. Please select a different time.",
        );
        return;
      }
    }

    stageDates[dateIndex].timeSlots[slotIndex] = value;
    setProposedDatesByStage({
      ...proposedDatesByStage,
      [stageIndex]: stageDates,
    });
  };

  const handleStageNameChange = (stageIndex, value) => {
    // Create updated stages array first
    const updatedStages = selectedInterviewForProposeSlot.stages.map(
      (stage, idx) =>
        idx === stageIndex ? { ...stage, stageName: value } : stage,
    );

    // Update the state
    setSelectedInterviewForProposeSlot((prev) => ({
      ...prev,
      stages: updatedStages,
    }));

    // Check for duplicate stage names in the updated array
    if (value.trim() !== "") {
      const isDuplicate = updatedStages.some(
        (stage, idx) =>
          idx !== stageIndex &&
          stage.stageName?.trim().toLowerCase() === value.trim().toLowerCase(),
      );

      if (isDuplicate) {
        toast.warning(
          "Stage name already exists. Please use a different name.",
        );
      }
    }
  };

  const handleInterviewerChange = (stageIndex, value, subIndex = null) => {
    setSelectedInterviewForProposeSlot((prev) => {
      const updatedStages = [...prev.stages];
      const stage = updatedStages[stageIndex];

      // Initialize interviewers if not present
      let interviewers = stage.interviewers
        ? [...stage.interviewers]
        : [stage.interviewer || ""];

      if (subIndex !== null) {
        interviewers[subIndex] = value;
      } else {
        interviewers[0] = value;
      }

      updatedStages[stageIndex] = { ...stage, interviewers };
      return { ...prev, stages: updatedStages };
    });
  };

  const handleProposeAddInterviewer = (stageIndex) => {
    setSelectedInterviewForProposeSlot((prev) => {
      const updatedStages = [...prev.stages];
      const stage = updatedStages[stageIndex];
      const interviewers = stage.interviewers
        ? [...stage.interviewers]
        : [stage.interviewer || ""];
      interviewers.push("");
      updatedStages[stageIndex] = { ...stage, interviewers };
      return { ...prev, stages: updatedStages };
    });
  };

  const handleProposeRemoveInterviewer = (stageIndex, interviewerIndex) => {
    setSelectedInterviewForProposeSlot((prev) => {
      const updatedStages = [...prev.stages];
      const stage = updatedStages[stageIndex];
      let interviewers = stage.interviewers
        ? [...stage.interviewers]
        : [stage.interviewer || ""];
      interviewers = interviewers.filter((_, i) => i !== interviewerIndex);
      updatedStages[stageIndex] = { ...stage, interviewers };
      return { ...prev, stages: updatedStages };
    });
  };

  const handleAddNewStage = () => {
    if (!selectedInterviewForProposeSlot) return;

    const stages = selectedInterviewForProposeSlot.stages || [];
    const lastStageIndex = stages.length - 1;

    // Check if previous stage is completed before adding new
    if (lastStageIndex >= 0) {
      const lastStage = stages[lastStageIndex];
      const isCompleted =
        lastStage.stageFeedback && lastStage.stageFeedback.trim().length > 0;

      if (!isCompleted) {
        toast.warning(
          "Please complete the current stage before adding a new one.",
        );
        return;
      }
    }

    const newStage = {
      stageName: "",
      interviewers: [""],
      date: null,
      time: null,
      isNewStage: true, // Flag to identify unsaved stage
    };

    const updatedInterview = {
      ...selectedInterviewForProposeSlot,
      stages: [...stages, newStage],
    };

    setSelectedInterviewForProposeSlot(updatedInterview);

    // Initialize dates for new stage
    setProposedDatesByStage((prev) => ({
      ...prev,
      [stages.length]: [
        {
          date: null,
          timeSlots: [""],
        },
      ],
    }));
  };

  // Schedule Dialog Handler Functions (for Add New Round / Reschedule)
  const handleScheduleAddDate = (stageIndex) => {
    const stageDates = [...(scheduleDatesByStage[stageIndex] || [])];
    const lastDateObj = stageDates[stageDates.length - 1];

    // Validate current active input before adding another date
    if (lastDateObj) {
      const lastSlot = lastDateObj.timeSlots[lastDateObj.timeSlots.length - 1];

      // If both date and time are filled, check for duplicates before committing
      if (lastDateObj.date && lastSlot && lastSlot.trim() !== "") {
        const currentDateStr = formatDateForInput(lastDateObj.date);
        // Check for duplicate time slot across all dates in this stage
        const otherSlots = lastDateObj.timeSlots.slice(0, -1);
        if (otherSlots.includes(lastSlot)) {
          toast.error(
            "This time slot already exists for this date. Please select a different time.",
          );
          return;
        }
        for (let i = 0; i < stageDates.length - 1; i++) {
          if (
            stageDates[i].date &&
            formatDateForInput(stageDates[i].date) === currentDateStr
          ) {
            if (stageDates[i].timeSlots.includes(lastSlot)) {
              toast.error(
                "This time slot already exists for this date. Please select a different time.",
              );
              return;
            }
          }
        }
      }
    }

    setScheduleDatesByStage({
      ...scheduleDatesByStage,
      [stageIndex]: [
        ...(scheduleDatesByStage[stageIndex] || []),
        {
          date: null,
          timeSlots: [""],
        },
      ],
    });
  };

  const handleScheduleRemoveDate = (stageIndex, dateIndex) => {
    const stageDates = scheduleDatesByStage[stageIndex] || [];
    setScheduleDatesByStage({
      ...scheduleDatesByStage,
      [stageIndex]: stageDates.filter((_, index) => index !== dateIndex),
    });
  };

  const handleScheduleAddTimeSlot = (stageIndex, dateIndex) => {
    const stageDates = [...(scheduleDatesByStage[stageIndex] || [])];
    const currentSlots = stageDates[dateIndex].timeSlots;
    const lastSlot = currentSlots[currentSlots.length - 1];

    // Don't add if current slot is empty
    if (!lastSlot || lastSlot.trim() === "") {
      toast.error(
        "Please fill in the current time slot before adding another.",
      );
      return;
    }

    // Don't add if date is not selected
    if (!stageDates[dateIndex].date) {
      toast.error("Please select a date before adding another time slot.");
      return;
    }

    // Check for duplicate time slot within the same date
    const otherSlots = currentSlots.slice(0, -1);
    if (otherSlots.includes(lastSlot)) {
      toast.error(
        "This time slot already exists for this date. Please select a different time.",
      );
      return;
    }

    // Check for duplicate time slot across all dates in this stage
    const currentDateStr = formatDateForInput(stageDates[dateIndex].date);
    for (let i = 0; i < stageDates.length; i++) {
      if (i === dateIndex) continue;
      if (
        stageDates[i].date &&
        formatDateForInput(stageDates[i].date) === currentDateStr
      ) {
        if (stageDates[i].timeSlots.includes(lastSlot)) {
          toast.error(
            "This time slot already exists for this date in another entry. Please select a different time.",
          );
          return;
        }
      }
    }

    stageDates[dateIndex].timeSlots.push("");
    setScheduleDatesByStage({
      ...scheduleDatesByStage,
      [stageIndex]: stageDates,
    });
  };

  const handleScheduleRemoveTimeSlot = (stageIndex, dateIndex, slotIndex) => {
    const stageDates = [...(scheduleDatesByStage[stageIndex] || [])];
    stageDates[dateIndex].timeSlots = stageDates[dateIndex].timeSlots.filter(
      (_, index) => index !== slotIndex,
    );
    setScheduleDatesByStage({
      ...scheduleDatesByStage,
      [stageIndex]: stageDates,
    });
  };

  const handleScheduleDateChange = (stageIndex, dateIndex, newDate) => {
    const stageDates = [...(scheduleDatesByStage[stageIndex] || [])];
    const targetDateObj = stageDates[dateIndex];

    // Check for duplicate dates in the same stage
    if (newDate) {
      const newDateStr = formatDateForInput(newDate);
      const isDuplicate = stageDates.some(
        (dateObj, idx) =>
          idx !== dateIndex &&
          dateObj.date &&
          formatDateForInput(dateObj.date) === newDateStr,
      );

      if (isDuplicate) {
        toast.warning(
          "This date has already been added for this stage. Please select a different date.",
        );
      }
    }

    // If the active dateObj has committed chips (timeSlots > 1), we shouldn't mutate
    // the date for the existing chips. Instead, we detach the active input into a new dateObj.
    if (targetDateObj.timeSlots.length > 1) {
      // 1. Remove the active slot from the current dateObj
      const activeSlot = targetDateObj.timeSlots.pop();

      // 2. Append a new dateObj with the newDate and the activeSlot
      stageDates.push({
        date: newDate,
        timeSlots: [activeSlot],
      });
    } else {
      // It's just the active input, safe to mutate the date directly
      targetDateObj.date = newDate;
    }

    setScheduleDatesByStage({
      ...scheduleDatesByStage,
      [stageIndex]: stageDates,
    });
  };

  const handleScheduleTimeSlotChange = (
    stageIndex,
    dateIndex,
    slotIndex,
    value,
  ) => {
    const stageDates = [...(scheduleDatesByStage[stageIndex] || [])];

    // Check for duplicate time slots within the same date and stage
    if (value && stageDates[dateIndex]?.timeSlots) {
      const isDuplicateInSameDate = stageDates[dateIndex].timeSlots.some(
        (slot, idx) => idx !== slotIndex && slot === value,
      );

      if (isDuplicateInSameDate) {
        toast.error(
          "This time slot has already been added for this date. Please select a different time.",
        );
        return;
      }
    }

    // Check for duplicate time slots across different stages on the same date
    if (value && stageDates[dateIndex]?.date) {
      const currentDate = formatDateForInput(stageDates[dateIndex].date);

      const isDuplicateTimeAcrossStages = Object.keys(
        scheduleDatesByStage,
      ).some((stgIdx) => {
        if (parseInt(stgIdx) === stageIndex) return false; // Skip current stage
        const otherStageDates = scheduleDatesByStage[stgIdx] || [];

        return otherStageDates.some((dateObj) => {
          if (
            !dateObj.date ||
            formatDateForInput(dateObj.date) !== currentDate
          ) {
            return false; // Different date, no conflict
          }

          // Same date, check if any time slot matches
          return dateObj.timeSlots.some((slot) => slot === value);
        });
      });

      if (isDuplicateTimeAcrossStages) {
        toast.error(
          "This time slot has already been selected for another stage on the same date. Please select a different time.",
        );
        return;
      }
    }

    stageDates[dateIndex].timeSlots[slotIndex] = value;
    setScheduleDatesByStage({
      ...scheduleDatesByStage,
      [stageIndex]: stageDates,
    });
  };

  const handleSaveProposedSlots = async () => {
    if (!selectedInterviewForProposeSlot) return;

    // Calculate active stage index (same logic as open dialog)
    let activeIndex = 0;
    if (
      selectedInterviewForProposeSlot.stages &&
      selectedInterviewForProposeSlot.stages.length > 0
    ) {
      const foundIndex = selectedInterviewForProposeSlot.stages.findIndex(
        (stage) => {
          // Priority 1: Check if the isActive flag was injected (most reliable)
          if (stage.isActive) return true;

          // Priority 2: Use status if available
          if (stage.status) {
            return stage.status !== "completed";
          }

          // Priority 3: Fallback to legacy feedback check
          const isCompleted =
            stage.stageFeedback && stage.stageFeedback.trim().length > 0;
          return !isCompleted;
        },
      );
      activeIndex =
        foundIndex !== -1
          ? foundIndex
          : selectedInterviewForProposeSlot.stages.length;
    }

    // If the interview status is 'slot_rejected', we might need to find the rejected stage
    if (selectedInterviewForProposeSlot.status === "slot_rejected") {
      const rejectedIndex = selectedInterviewForProposeSlot.stages.findIndex(
        (s) => s.candidateAction === "rejected",
      );
      if (rejectedIndex !== -1) {
        activeIndex = rejectedIndex;
      }
    }

    // Validate ONLY the active stage
    const activeStage = selectedInterviewForProposeSlot.stages[activeIndex];

    // If active stage doesn't exist (shouldn't happen if we added it correctly), return
    if (
      !activeStage &&
      activeIndex < selectedInterviewForProposeSlot.stages.length
    ) {
      toast.error("Active stage not found");
      return;
    }

    // 1. Validate Stage Name & Interviewer for Active Stage
    if (
      !activeStage.stageName?.trim() ||
      !activeStage.interviewers ||
      activeStage.interviewers.filter((i) => i.trim() !== "").length === 0
    ) {
      toast.error(
        "Please provide stage name and interviewer name for the current stage",
      );
      return;
    }

    // 2. Validate Duplicate Stage Name (check against other stages)
    const otherStageNames = selectedInterviewForProposeSlot.stages
      .filter((_, idx) => idx !== activeIndex)
      .map((s) => s.stageName?.trim().toLowerCase())
      .filter(Boolean);

    if (otherStageNames.includes(activeStage.stageName.trim().toLowerCase())) {
      toast.error("Stage name already exists. Please use a different name.");
      return;
    }

    // 3. Validate Dates & Time Slots for Active Stage
    const activeStageDates = proposedDatesByStage[activeIndex];

    // Strip trailing empty "active input" placeholder entries before validation
    // (the chip UI always keeps one empty entry at the end for new input)
    let cleanedStageDates = activeStageDates
      .map((dateObj) => {
        // Remove trailing empty time slots
        const cleanedSlots = [...dateObj.timeSlots];
        while (
          cleanedSlots.length > 0 &&
          (!cleanedSlots[cleanedSlots.length - 1] ||
            cleanedSlots[cleanedSlots.length - 1].trim() === "")
        ) {
          cleanedSlots.pop();
        }
        return { ...dateObj, timeSlots: cleanedSlots };
      })
      // Only keep dateObjs that have BOTH a date AND at least one filled time slot
      .filter(
        (dateObj) =>
          dateObj.date && dateObj.timeSlots.some((s) => s && s.trim() !== ""),
      );

    if (!cleanedStageDates || cleanedStageDates.length === 0) {
      toast.error(
        "Please add at least one date with a time slot for the current stage",
      );
      return;
    }

    const hasEmptyDate = cleanedStageDates.some((dateObj) => !dateObj.date);
    if (hasEmptyDate) {
      toast.error(
        "Please fill in all date fields before proposing time slots.",
      );
      return;
    }

    for (const dateObj of cleanedStageDates) {
      const hasEmptyTimeSlot = dateObj.timeSlots.some(
        (slot) => !slot || slot.trim() === "",
      );
      if (hasEmptyTimeSlot) {
        toast.error(
          "Please fill in all time slot fields before proposing time slots.",
        );
        return;
      }
    }

    // Check for duplicate time slots across all entries for the same date
    const dateSlotPairs = [];
    for (const dateObj of cleanedStageDates) {
      const dateStr = formatDateForInput(dateObj.date);
      for (const slot of dateObj.timeSlots) {
        if (slot && slot.trim()) {
          dateSlotPairs.push(`${dateStr}-${slot.trim()}`);
        }
      }
    }
    const uniqueDateSlotPairs = new Set(dateSlotPairs);
    if (uniqueDateSlotPairs.size !== dateSlotPairs.length) {
      toast.error(
        "Duplicate time slots found in the current stage. Please select different times for the same date.",
      );
      return;
    }

    // Check for cross-stage conflicts (same date and time)
    // We need to compare activeStageDates with proposedDatesByStage for OTHER stages
    // But proposedDatesByStage might not have other stages loaded if we didn't open them?
    // Actually, proposedDatesByStage is initialized for all stages in handleOpenProposeSlotDialog.
    // So we can check against it.

    for (const dateObj of cleanedStageDates) {
      if (!dateObj.date) continue;
      const dateStr = formatDateForInput(dateObj.date);

      for (const otherStageIndexStr in proposedDatesByStage) {
        const otherStageIndex = parseInt(otherStageIndexStr);
        if (otherStageIndex === activeIndex) continue; // Skip current stage

        const otherStageDates = proposedDatesByStage[otherStageIndex] || [];
        for (const otherDateObj of otherStageDates) {
          if (
            otherDateObj.date &&
            formatDateForInput(otherDateObj.date) === dateStr
          ) {
            // Same date, check for overlapping times
            const otherTimeSlots = otherDateObj.timeSlots;
            const myTimeSlots = dateObj.timeSlots;

            const hasOverlap = myTimeSlots.some((mySlot) =>
              otherTimeSlots.includes(mySlot),
            );

            if (hasOverlap) {
              toast.error(
                `Time slot conflict detected with Stage ${otherStageIndex + 1
                } on ${new Date(
                  dateObj.date,
                ).toLocaleDateString()}. Please select different times.`,
              );
              return;
            }
          }
        }
      }
    }

    try {
      // Generate unique slot IDs and format the proposed slots for this stage
      // Group time slots by date to avoid duplicate date blocks in the payload
      const datesMap = new Map();
      cleanedStageDates.forEach((dateObj) => {
        const formattedDate = formatDateForInput(dateObj.date);
        if (!datesMap.has(formattedDate)) datesMap.set(formattedDate, []);

        dateObj.timeSlots
          .filter((slot) => slot.trim() !== "")
          .forEach((slot) => {
            datesMap.get(formattedDate).push(slot);
          });
      });

      // Generate unique slot IDs and format the proposed slots for this stage
      const proposedSlots = Array.from(datesMap.entries()).map(
        ([formattedDate, timeSlots]) => {
          const slots = timeSlots.map((slot, index) => {
            return {
              slotId: `${activeStage.stageName
                .replace(/\s+/g, "-")
                .toLowerCase()}-${formattedDate}-${index + 1}`,
              time: formatTimeForAPI(slot),
            };
          });

          return {
            date: formattedDate,
            slots: slots,
          };
        },
      );

      const response = await fetch(
        `${API_BASE}/api/job-postings/interviews/${selectedInterviewForProposeSlot.interviewId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: activeStage.isNewStage ? "create_stage" : "propose_slots",
            data: {
              proposedSlots: proposedSlots,
              ...(activeStage.isNewStage && {
                stageName: activeStage.stageName,
                interviewers: activeStage.interviewers
                  ? activeStage.interviewers.filter((iv) => iv.trim() !== "")
                  : [activeStage.interviewer].filter(
                    (iv) => iv && iv.trim() !== "",
                  ),
              }),
            },
          }),
        },
      );

      if (response.ok) {
        toast.success("Time slots proposed successfully");
        handleCloseProposeSlotDialog();
        await fetchIndividualInterview(
          selectedInterviewForProposeSlot.interviewId,
        );
      } else {
        toast.error("Failed to propose time slots");
      }
    } catch (error) {
      console.error("Error proposing time slots:", error);
      toast.error("Error proposing time slots");
    }
  };

  const filteredInterviews = interviews.filter(
    (interview) => statusFilter === "all" || interview.status === statusFilter,
  );

  const allStagesCompleted = stages.every(
    (stage) => stage.stageFeedback && stage.stageFeedback.trim().length > 0,
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading interviews...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", mx: "auto", p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}
        >
          Interviews
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Review and manage interview processes for candidates
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Filter by Status:
          </Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Interviews</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="slot_selected">Slot Selected</MenuItem>
              <MenuItem value="propose_new_slots">Propose New Slots</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="slot_rejected">Slot Rejected</MenuItem>
              <MenuItem value="slots_proposed">Slots Proposed</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="requested">Requested</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="declined">Declined</MenuItem>
            </Select>
          </FormControl>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: "auto" }}
          >
            {filteredInterviews.length} interview
            {filteredInterviews.length !== 1 ? "s" : ""} found
          </Typography>
        </Box>
      </Paper>

      {/* Interviews Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Career Journey</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                Stage
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                Interview Date & Time
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Decision</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInterviews.map((interview) => {
              const activeStage = getActiveStage(interview);
              const candidateName = getCandidateName(interview.candidate);
              console.log("check if peerreviewed", interview.candidate.peerReview);

              return (
                <TableRow key={interview.interviewId} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">

                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                        <PersonIcon color="action" fontSize="small" />

                        {interview.candidate?.peerReview && (
                          <Tooltip title="Hiring Manager Evaluated">
                            <VerifiedIcon
                              sx={{
                                position: "absolute",
                                bottom: -4,
                                right: -4,
                                fontSize: 14,
                                color: "#7b1fa2",
                                bgcolor: "background.paper",
                                borderRadius: "50%",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {candidateName}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                          }}
                        >
                          {interview.candidate?.email}
                        </Typography>
                      </Box>

                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleOpenCareerJourney(interview)}
                      sx={{
                        backgroundColor: "#9b42e9",
                        color: "#FFFFFF",
                        textTransform: "none",
                        fontWeight: 800,
                        borderRadius: 2,
                        fontSize: "0.80rem",
                        padding: "4px 10px",
                        minHeight: "24px",
                        "& .MuiButton-startIcon": {
                          marginRight: "4px",
                        },
                        '&:hover': {
                          backgroundColor: "#9b42e9",
                          color: "#FFFFFF",
                        },
                      }}
                    >
                      View Career Journey
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(interview.status === "slot_rejected"
                        ? "not_available"
                        : interview.interestStatus || interview.status || "N/A"
                      )
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (char) => char.toUpperCase())}
                      size="small"
                      sx={{
                        ...getStatusColor(
                          interview.interestStatus
                            ? interview.interestStatus
                            : interview.status,
                        ),
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {interview.status === "cancelled" ? (
                      <Typography variant="body2" sx={{ color: "#ba68c8" }}>
                        {interview.cancellationDetails?.cancelled_by ===
                          "candidate"
                          ? "Candidate Cancelled"
                          : "Interview Cancelled"}
                      </Typography>
                    ) : interview.status === "declined" ? (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    ) : interview.status === "completed" &&
                      interview.overallResult ? (
                      <Typography variant="body2" color="text.secondary">
                        Process Completed
                      </Typography>
                    ) : activeStage ? (
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        {activeStage.stageName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not set
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {interview.status === "cancelled" ||
                      interview.status === "declined" ||
                      (interview.status === "completed" &&
                        interview.overallResult) ? (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    ) : activeStage ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <ScheduleIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {activeStage.date
                              ? new Date(activeStage.date).toLocaleDateString()
                              : "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {activeStage.time || "Time not set"}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not scheduled
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {interview.status === "cancelled" ? (
                      <Chip
                        label="Cancelled"
                        size="small"
                        sx={{
                          backgroundColor: "#ba68c8",
                          color: "#FFFFFF",
                          fontWeight: 600,
                        }}
                      />
                    ) : (
                      <Chip
                        label={formatDecision(interview.overallResult)}
                        size="small"
                        sx={{
                          ...getDecisionColor(interview.overallResult),
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {interview.createdAt
                        ? new Date(interview.createdAt).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(interview)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Actions">
                        <IconButton
                          size="small"
                          color="secondary"
                          disabled={
                            interview.status === "cancelled" ||
                            interview.status === "admin_declined" ||
                            interview.interestStatus === "admin_declined"
                          }
                          onClick={(e) => {
                            setActionMenuAnchor(e.currentTarget);
                            setSelectedInterviewForAction(interview);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredInterviews.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center", mt: 3 }}>
          <Typography variant="h6" color="text.secondary">
            No interviews found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Interviews will appear here once candidates are scheduled
          </Typography>
        </Paper>
      )}

      {/* Interview Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#6c5ce7",
            color: "white",
          }}
        >
          <PersonIcon color="white" />

          <Typography variant="h6">
            Interview Details,{" "}
            {selectedInterview && getCandidateName(selectedInterview.candidate)}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <CloseIcon onClick={handleCloseDialog} sx={{ cursor: "pointer" }} />
        </DialogTitle>

        <DialogContent
          sx={{
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          {selectedInterview && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Candidate Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedInterview.candidate?.email || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Current Position
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedInterview.candidate?.currentPosition || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Interview Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={(selectedInterview.status === "slot_rejected"
                        ? "not_available"
                        : selectedInterview.status
                      )
                        .replace(/_/g, " ")
                        .replace(
                          /\b\w/g,
                          (char) => char.toUpperCase() || "N/A",
                        )}
                      size="small"
                      sx={{
                        ...getStatusColor(selectedInterview.status),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Overall Decision
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={formatDecision(selectedInterview.overallResult)}
                      size="small"
                      sx={{
                        ...getDecisionColor(selectedInterview.overallResult),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Grid>
                {selectedInterview.cancellationDetails && (
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: "#f3e5f5",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "#ba68c8",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "#ba68c8" }}
                      >
                        Cancellation Details
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        {selectedInterview.cancellationDetails.cancelled_by ===
                          "corporate"
                          ? "You cancelled this interview."
                          : selectedInterview.cancellationDetails
                            .cancelled_by === "candidate"
                            ? "The candidate cancelled this interview."
                            : "This interview was cancelled."}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Reason:</strong>{" "}
                        {selectedInterview.cancellationDetails.reason}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Cancelled At:</strong>{" "}
                        {new Date(
                          selectedInterview.cancellationDetails.cancelled_at,
                        ).toLocaleString()}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {selectedInterview.status === "declined" && (
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        backgroundColor: "#f3e5f5",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "#ba68c8",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "#ba68c8" }}
                      >
                        Request Declined
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        The candidate has declined the interview request.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12}>
                  {selectedInterview.overallFeedback?.feedback && (
                    <Box
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        backgroundColor: `${getStatusColor(selectedInterview.status).backgroundColor}15`,
                        border: `1px solid ${getStatusColor(selectedInterview.status).backgroundColor}40`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: getStatusColor(selectedInterview.status)
                            .backgroundColor,
                        }}
                      >
                        Overall Internal Feedback
                      </Typography>
                      <Typography variant="body2">
                        {selectedInterview.overallFeedback.feedback}
                      </Typography>
                    </Box>
                  )}
                  {selectedInterview.overallFeedback?.candidateFeedback && (
                    <Box
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        backgroundColor: `${getStatusColor(selectedInterview.status).backgroundColor}15`,
                        border: `1px solid ${getStatusColor(selectedInterview.status).backgroundColor}40`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: getStatusColor(selectedInterview.status)
                            .backgroundColor,
                        }}
                      >
                        Overall Candidate Feedback
                      </Typography>
                      <Typography variant="body2">
                        {selectedInterview.overallFeedback.candidateFeedback}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Interview Stages
                  </Typography>
                  {selectedInterview.stages &&
                    selectedInterview.stages.length > 0 ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {selectedInterview.stages.map((stage, index) => {
                        const feedbackStatus =
                          selectedInterview.stageFeedbackStatus?.find(
                            (s) => s.stageName === stage.stageName,
                          );
                        const hasFeedback =
                          stage.stageFeedback &&
                          stage.stageFeedback.trim().length > 0;
                        const canAddFeedback = canStartNextStage(
                          selectedInterview,
                          index,
                        );
                        const isBlocked = !canAddFeedback && !hasFeedback;
                        const isNotStated =
                          selectedInterview.status === "requested" ||
                          selectedInterview.status === "scheduled";

                        return (
                          <Paper
                            key={index}
                            sx={{
                              p: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 1,
                                alignItems: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Round {index + 1}: {stage.stageName}
                                </Typography>
                                {isStageCompleted(stage) && (
                                  <Chip
                                    label={`Feedback added: ${stage.candidateStageFeedback &&
                                      stage.candidateStageFeedback.trim()
                                        .length > 0
                                      ? "✓"
                                      : "Pending"
                                      }`}
                                    sx={
                                      stage.candidateStageFeedback &&
                                        stage.candidateStageFeedback.trim()
                                          .length > 0
                                        ? {
                                          backgroundColor: "#9b42e9",
                                          color: "#FFFFFF",
                                        }
                                        : {
                                          backgroundColor: "#ba68c8",
                                          color: "#FFFFFF",
                                        }
                                    }
                                    size="small"
                                    icon={
                                      <CheckCircleIcon
                                        style={{ color: "white" }}
                                      />
                                    }
                                  />
                                )}
                                {index > 0 &&
                                  !canAddFeedback &&
                                  !isStageCompleted(stage) &&
                                  selectedInterview.status !== "cancelled" && (
                                    <Chip
                                      label="Previous feedback required"
                                      size="small"
                                      sx={{
                                        backgroundColor: "#ba68c8",
                                        color: "#FFFFFF",
                                      }}
                                    />
                                  )}
                              </Box>
                              {feedbackStatus &&
                                !isStageCompleted(stage) &&
                                selectedInterview.status !== "cancelled" && (
                                  <Chip
                                    label={
                                      feedbackStatus.feedbackAdded
                                        ? "Feedback Added"
                                        : "Pending Feedback"
                                    }
                                    sx={
                                      feedbackStatus.feedbackAdded
                                        ? {
                                          backgroundColor: "#9B42E9",
                                          color: "#ffffff",
                                        }
                                        : {}
                                    }
                                    size="small"
                                  />
                                )}
                            </Box>
                            {(stage.interviewer ||
                              (stage.interviewers &&
                                stage.interviewers.length > 0)) && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 1 }}
                                >
                                  <strong>
                                    Interviewer
                                    {stage.interviewers &&
                                      stage.interviewers.length > 1
                                      ? "s"
                                      : ""}
                                    :
                                  </strong>{" "}
                                  {stage.interviewers &&
                                    stage.interviewers.length > 0
                                    ? stage.interviewers.join(", ")
                                    : stage.interviewer}
                                </Typography>
                              )}
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              <strong>Date:</strong>{" "}
                              {stage.date
                                ? new Date(stage.date).toLocaleDateString()
                                : "N/A"}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              <strong>Time:</strong> {stage.time || "N/A"}
                            </Typography>
                            {/* {stage.stageFeedback && (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1.5,
                                  bgcolor: "white",
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Feedback:
                                </Typography>
                                <Typography variant="body2">
                                  {stage.stageFeedback}
                                </Typography>
                              </Box>
                            )} */}

                            {stage.candidateStageFeedback && (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1.5,
                                  bgcolor: "white",
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Candidate Feedback:
                                </Typography>
                                <Typography variant="body2">
                                  {stage.candidateStageFeedback}
                                </Typography>
                              </Box>
                            )}

                            {(selectedInterview.status === "in_progress" ||
                              selectedInterview.status === "scheduled") && (
                                <Box
                                  sx={{
                                    mt: 2,
                                    display: "flex",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                  }}
                                >
                                  {!isStageCompleted(stage) ? (
                                    <>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() =>
                                          handleMarkStageComplete(
                                            selectedInterview,
                                            stage,
                                          )
                                        }
                                        disabled={isBlocked || isNotStated}
                                        sx={{
                                          backgroundColor: "#9b42e9",
                                          "&:hover": {
                                            backgroundColor: "#8c5eb4ff",
                                          },
                                        }}
                                      >
                                        Mark Stage Complete
                                      </Button>
                                      {isNotStated && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: "block" }}
                                        >
                                          Cannot add feedback before starting
                                          interview
                                        </Typography>
                                      )}
                                      {!isNotStated && isBlocked && (
                                        <Typography
                                          variant="caption"
                                          color="error"
                                          sx={{ display: "block" }}
                                        >
                                          Complete previous stages first
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <Chip
                                        label="Stage Completed"
                                        sx={{
                                          backgroundColor: "#9b42e9",
                                          color: "#FFFFFF",
                                        }}
                                        size="small"
                                        icon={
                                          <CheckCircleIcon
                                            style={{ color: "white" }}
                                          />
                                        }
                                      />
                                    </>
                                  )}
                                </Box>
                              )}
                          </Paper>
                        );
                      })}
                      {(selectedInterview.status === "scheduled" ||
                        selectedInterview.status === "in_progress") &&
                        selectedInterview.stages &&
                        selectedInterview.stages.every((s) =>
                          isStageCompleted(s),
                        ) && (
                          <Box
                            sx={{
                              mt: 3,
                              p: 2,
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, mb: 1 }}
                            >
                              Next Steps
                            </Typography>
                            <Box
                              sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                            >
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  handleOpenScheduleDialog(selectedInterview)
                                }
                                color="primary"
                              >
                                Add New Stage
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={<CheckCircleIcon />}
                                onClick={() =>
                                  handleCompleteInterviewWithFeedback(
                                    selectedInterview,
                                  )
                                }
                                sx={{
                                  backgroundColor: "#9b42e9",
                                  "&:hover": { backgroundColor: "#8c5eb4ff" },
                                }}
                              >
                                Complete Interview & Add Overall Feedback
                              </Button>
                            </Box>
                          </Box>
                        )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No stages scheduled yet
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Interview Dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={handleCloseScheduleDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#6c5ce7",
            color: "white",
          }}
        >
          <ScheduleIcon color="white" />
          <Typography variant="h6">
            {selectedInterviewForSchedule?.status === "in_progress"
              ? "Add New Interview Stage"
              : selectedInterviewForSchedule?.status === "scheduled"
                ? "Reschedule Interview"
                : "Schedule Interview"}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />

          <CloseIcon
            onClick={handleCloseScheduleDialog}
            sx={{ cursor: "pointer" }}
          />
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          {selectedInterviewForSchedule && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Candidate:{" "}
                {getCandidateName(selectedInterviewForSchedule.candidate)}
              </Typography>
              {selectedInterviewForSchedule.status === "in_progress" && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You can add new interview stages to the existing schedule.
                  Existing stages will be preserved.
                </Alert>
              )}
              {selectedInterviewForSchedule.status === "in_progress" &&
                selectedInterviewForSchedule.stages &&
                selectedInterviewForSchedule.stages.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Existing Stages:
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {selectedInterviewForSchedule.stages.map((stage, idx) => (
                        <Chip
                          key={idx}
                          label={`${stage.stageName} - ${stage.date
                            ? new Date(stage.date).toLocaleDateString()
                            : "N/A"
                            }`}
                          color="default"
                          size="small"
                          sx={{ width: "fit-content" }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                {selectedInterviewForSchedule.status === "in_progress"
                  ? "New Interview Round"
                  : "Interview Stages"}
              </Typography>
              <Stack spacing={2}>
                {stages.map((stage, index) => {
                  const stageDates = scheduleDatesByStage[index] || [];

                  return (
                    <Paper key={index} sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {selectedInterviewForSchedule.status === "in_progress"
                            ? "New Stage"
                            : `Stage ${index + 1}`}
                        </Typography>
                        {stages.length > 1 && !stage.locked && (
                          <IconButton
                            size="small"
                            sx={{ color: "#ba68c8" }}
                            onClick={() => handleRemoveStage(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Stage Name"
                            value={stage.stageName}
                            onChange={(e) =>
                              handleStageChange(
                                index,
                                "stageName",
                                e.target.value,
                              )
                            }
                            placeholder="e.g., Technical Round, Manager Round"
                            required
                            disabled={stage.locked}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack spacing={1}>
                            {stage.interviewers &&
                              stage.interviewers.length > 0 ? (
                              <TextField
                                fullWidth
                                label="Interviewer Name"
                                value={
                                  stage.interviewers[
                                  stage.interviewers.length - 1
                                  ]
                                }
                                onChange={(e) =>
                                  handleStageChange(
                                    index,
                                    "interviewers",
                                    e.target.value,
                                    stage.interviewers.length - 1,
                                  )
                                }
                                placeholder="Type interviewer name"
                                required={
                                  stage.interviewers.filter(
                                    (iv) => iv.trim() !== "",
                                  ).length === 0
                                }
                                disabled={stage.locked}
                              />
                            ) : (
                              // Fallback if interviewers array is empty/undefined (shouldn't happen with correct init)
                              <TextField
                                fullWidth
                                label="Interviewer"
                                value={stage.interviewer || ""}
                                onChange={(e) =>
                                  handleStageChange(
                                    index,
                                    "interviewer",
                                    e.target.value,
                                  )
                                }
                                placeholder="Interviewer name"
                                required
                                disabled={stage.locked}
                              />
                            )}
                            {!stage.locked && (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box
                                  sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                    alignItems: "center",
                                    minHeight: 32,
                                  }}
                                >
                                  {stage.interviewers &&
                                    stage.interviewers
                                      .slice(0, -1)
                                      .map((interviewer, i) =>
                                        interviewer.trim() !== "" ? (
                                          <Chip
                                            key={i}
                                            label={interviewer}
                                            size="small"
                                            icon={
                                              <PersonIcon
                                                sx={{ fontSize: 16 }}
                                              />
                                            }
                                            onClick={() => {
                                              const updatedInterviewers = [
                                                ...stage.interviewers,
                                              ];
                                              const clickedValue =
                                                updatedInterviewers[i];
                                              updatedInterviewers.splice(i, 1);
                                              // Make the clicked chip the current input by placing it at the end instead of the empty string
                                              if (
                                                updatedInterviewers.length >
                                                0 &&
                                                updatedInterviewers[
                                                updatedInterviewers.length - 1
                                                ] === ""
                                              ) {
                                                updatedInterviewers[
                                                  updatedInterviewers.length - 1
                                                ] = clickedValue;
                                              } else {
                                                updatedInterviewers.push(
                                                  clickedValue,
                                                );
                                              }
                                              handleStageChange(
                                                index,
                                                "interviewers",
                                                updatedInterviewers,
                                              );
                                            }}
                                            onDelete={() =>
                                              handleRemoveInterviewer(index, i)
                                            }
                                            sx={{
                                              backgroundColor: "#f3e5f5",
                                              color: "#7b1fa2",
                                              fontWeight: 500,
                                              maxWidth: 150,
                                              cursor: "pointer",
                                              "& .MuiChip-label": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                              },
                                              "& .MuiChip-icon": {
                                                color: "#9b42e9",
                                              },
                                              "& .MuiChip-deleteIcon": {
                                                color: "#ba68c8",
                                                "&:hover": { color: "#7b1fa2" },
                                              },
                                            }}
                                          />
                                        ) : null,
                                      )}
                                </Box>
                                <Button
                                  startIcon={<AddIcon />}
                                  onClick={() => handleAddInterviewer(index)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    flexShrink: 0,
                                    boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                                    borderColor: "#9b42e9",
                                    color: "#9b42e9",
                                    whiteSpace: "nowrap",
                                    "&:hover": {
                                      borderColor: "#7b1fa2",
                                      backgroundColor: "#f3e5f5",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                    },
                                  }}
                                >
                                  Add Interviewer
                                </Button>
                              </Box>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>

                      {/* Date and Time Slots Section */}
                      {!stage.locked && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            Date & Time Slots
                          </Typography>

                          {/* Active input: last dateObj's last time slot */}
                          {(() => {
                            const lastDateIndex = stageDates.length - 1;
                            const lastDateObj = stageDates[lastDateIndex];
                            if (!lastDateObj) return null;
                            const lastSlotIndex =
                              lastDateObj.timeSlots.length - 1;
                            const lastSlot =
                              lastDateObj.timeSlots[lastSlotIndex] ?? "";

                            return (
                              <Paper
                                sx={{
                                  p: 2,
                                  bgcolor: "white",
                                  border: "1px solid #e0e0e0",
                                  mb: 1.5,
                                }}
                              >
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <LocalizationProvider
                                      dateAdapter={AdapterDateFns}
                                    >
                                      <DatePicker
                                        label="Date"
                                        format="dd/MM/yyyy"
                                        minDate={today}
                                        value={lastDateObj.date}
                                        onChange={(newValue) =>
                                          handleScheduleDateChange(
                                            index,
                                            lastDateIndex,
                                            newValue,
                                          )
                                        }
                                        slotProps={{
                                          field: { clearable: true },
                                          textField: {
                                            fullWidth: true,
                                            required: true,
                                            inputProps: { readOnly: true },
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      label="Time Slot"
                                      type="time"
                                      value={lastSlot}
                                      onChange={(e) =>
                                        handleScheduleTimeSlotChange(
                                          index,
                                          lastDateIndex,
                                          lastSlotIndex,
                                          e.target.value,
                                        )
                                      }
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      required
                                    />
                                  </Grid>
                                </Grid>
                              </Paper>
                            );
                          })()}

                          {/* Chips row + Add buttons */}
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "flex-start",
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              sx={{
                                flex: 1,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                                alignItems: "center",
                                minHeight: 32,
                              }}
                            >
                              {/* Render chips for all completed date+time combos except the active input (last slot of last dateObj) */}
                              {stageDates.map((dateObj, dateIndex) => {
                                const isLastDate =
                                  dateIndex === stageDates.length - 1;
                                return dateObj.timeSlots.map(
                                  (slot, slotIndex) => {
                                    const isLastSlot =
                                      isLastDate &&
                                      slotIndex ===
                                      dateObj.timeSlots.length - 1;
                                    // Skip the active input entry (last slot of last dateObj)
                                    if (isLastSlot) return null;
                                    // Skip empty/incomplete entries
                                    if (!dateObj.date || !slot) return null;

                                    const dateStr = dateObj.date
                                      ? new Date(
                                        dateObj.date,
                                      ).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                      })
                                      : "";
                                    const timeStr = (() => {
                                      if (!slot) return "";
                                      const [h, m] = slot.split(":");
                                      const hr = parseInt(h, 10);
                                      const ampm = hr >= 12 ? "PM" : "AM";
                                      return `${hr % 12 || 12}:${m} ${ampm}`;
                                    })();

                                    return (
                                      <Chip
                                        key={`${dateIndex}-${slotIndex}`}
                                        label={`${dateStr} • ${timeStr}`}
                                        size="small"
                                        icon={
                                          <ScheduleIcon sx={{ fontSize: 16 }} />
                                        }
                                        onClick={() => {
                                          // Swap this chip with the active input (last slot of last dateObj)
                                          const newStageDates = stageDates.map(
                                            (d, di) => ({
                                              ...d,
                                              timeSlots: [...d.timeSlots],
                                            }),
                                          );

                                          const lastDI =
                                            newStageDates.length - 1;
                                          const lastSI =
                                            newStageDates[lastDI].timeSlots
                                              .length - 1;

                                          // Save current active input values
                                          const activeDate =
                                            newStageDates[lastDI].date;
                                          const activeSlot =
                                            newStageDates[lastDI].timeSlots[
                                            lastSI
                                            ];

                                          // Put clicked chip's values into the active input
                                          newStageDates[lastDI].date =
                                            dateObj.date;
                                          newStageDates[lastDI].timeSlots[
                                            lastSI
                                          ] = slot;

                                          // Put old active input values where the chip was
                                          newStageDates[dateIndex].date =
                                            activeDate;
                                          newStageDates[dateIndex].timeSlots[
                                            slotIndex
                                          ] = activeSlot;

                                          setScheduleDatesByStage({
                                            ...scheduleDatesByStage,
                                            [index]: newStageDates,
                                          });
                                        }}
                                        onDelete={() => {
                                          // Remove this time slot
                                          const newStageDates = stageDates.map(
                                            (d, di) => ({
                                              ...d,
                                              timeSlots: [...d.timeSlots],
                                            }),
                                          );
                                          newStageDates[dateIndex].timeSlots =
                                            newStageDates[
                                              dateIndex
                                            ].timeSlots.filter(
                                              (_, si) => si !== slotIndex,
                                            );
                                          // If no time slots left, remove the whole dateObj
                                          if (
                                            newStageDates[dateIndex].timeSlots
                                              .length === 0
                                          ) {
                                            newStageDates.splice(dateIndex, 1);
                                          }
                                          // Ensure at least one dateObj remains
                                          if (newStageDates.length === 0) {
                                            newStageDates.push({
                                              date: null,
                                              timeSlots: [""],
                                            });
                                          }
                                          setScheduleDatesByStage({
                                            ...scheduleDatesByStage,
                                            [index]: newStageDates,
                                          });
                                        }}
                                        sx={{
                                          backgroundColor: "#f3e5f5",
                                          color: "#7b1fa2",
                                          fontWeight: 500,
                                          cursor: "pointer",
                                          "& .MuiChip-icon": {
                                            color: "#9b42e9",
                                          },
                                          "& .MuiChip-deleteIcon": {
                                            color: "#ba68c8",
                                            "&:hover": { color: "#7b1fa2" },
                                          },
                                        }}
                                      />
                                    );
                                  },
                                );
                              })}
                            </Box>
                            <Box
                              sx={{ display: "flex", gap: 1, flexShrink: 0 }}
                            >
                              <Button
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  handleScheduleAddTimeSlot(
                                    index,
                                    stageDates.length - 1,
                                  )
                                }
                                size="small"
                                variant="outlined"
                                sx={{
                                  whiteSpace: "nowrap",
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                                  borderColor: "#9b42e9",
                                  color: "#9b42e9",
                                  "&:hover": {
                                    borderColor: "#7b1fa2",
                                    backgroundColor: "#f3e5f5",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                  },
                                }}
                              >
                                Add Time Slot
                              </Button>
                              <Button
                                startIcon={<AddIcon />}
                                onClick={() => handleScheduleAddDate(index)}
                                size="small"
                                variant="outlined"
                                sx={{
                                  whiteSpace: "nowrap",
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                                  borderColor: "#9b42e9",
                                  color: "#9b42e9",
                                  "&:hover": {
                                    borderColor: "#7b1fa2",
                                    backgroundColor: "#f3e5f5",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                  },
                                }}
                              >
                                Add Another Date
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
              {allStagesCompleted && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddStage}
                  sx={{ mt: 2 }}
                  variant="outlined"
                >
                  Add Another Stage
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseScheduleDialog}>Cancel</Button>
          <Button onClick={handleSaveSchedule} variant="contained">
            {selectedInterviewForSchedule?.status === "in_progress"
              ? "Add New Stage"
              : "Schedule Interview"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={handleCloseFeedbackDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#6c5ce7",
            color: "white",
          }}
        >
          <EditIcon color="white" />
          <Typography variant="h6">
            {selectedStageForFeedback?.feedback
              ? "Edit Stage Feedback"
              : "Add Stage Feedback"}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />

          <CloseIcon
            onClick={handleCloseFeedbackDialog}
            sx={{ cursor: "pointer" }}
          />
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          {selectedStageForFeedback && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {selectedStageForFeedback.feedback
                  ? "Update the feedback for this completed stage."
                  : "This stage has been marked as complete. Please provide feedback to proceed to the next round."}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Stage:</strong> {selectedStageForFeedback.stageName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Date:</strong>{" "}
                {selectedStageForFeedback.date
                  ? new Date(selectedStageForFeedback.date).toLocaleDateString()
                  : "N/A"}{" "}
                at {selectedStageForFeedback.time || "N/A"}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* <Alert severity="info" sx={{ py: 0 }}>
                  <strong>The information provided here is intended solely for internal corporate use.</strong>
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Internal Feedback"
                  value={stageFeedback}
                  onChange={(e) => setStageFeedback(e.target.value)}
                  placeholder="Enter detailed feedback for this interview stage. This feedback is required to proceed to the next round..."
                  required
                  helperText="Feedback is required to mark this stage as complete and enable the next round."
                /> */}

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Alert
                    severity="warning"
                    sx={{
                      py: 0,
                      mb: 2,
                      backgroundColor: "#FFF4E5",   // soft warning background
                      color: "#663C00",             // readable dark text
                      "& .MuiAlert-icon": {
                        color: "#ED6C02",           // warning orange icon
                      },
                    }}
                  >
                    <strong>
                      The feedback you provide here will be visible to the candidate.
                    </strong>
                  </Alert>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    label="Candidate Feedback"
                    value={candidateStageFeedback}
                    onChange={(e) => setCandidateStageFeedback(e.target.value)}
                    placeholder="Enter detailed of candidate feedback for this interview stage."
                    required
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeedbackDialog}>Cancel</Button>
          <Button
            onClick={handleSaveFeedback}
            variant="contained"
            disabled={!candidateStageFeedback.trim()}
            startIcon={<CheckCircleIcon />}
          >
            Save Feedback & Complete Stage
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stage Complete Confirmation Dialog */}
      <Dialog
        open={stageCompleteDialogOpen}
        onClose={handleCloseStageCompleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#6c5ce7",
            color: "white",
          }}
        >
          <CheckCircleIcon sx={{ color: "white" }} />
          <Typography variant="h6">Mark Stage Complete</Typography>
          <Box sx={{ flexGrow: 1 }} />

          <CloseIcon
            onClick={handleCloseStageCompleteDialog}
            sx={{ cursor: "pointer" }}
          />
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          {selectedStageForComplete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You are about to mark{" "}
                <strong>{selectedStageForComplete.stageName}</strong> as
                complete.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                After confirming, you will be prompted to add feedback for this
                stage. Feedback is required to proceed to the next round.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Date:{" "}
                {selectedStageForComplete.date
                  ? new Date(selectedStageForComplete.date).toLocaleDateString()
                  : "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Time: {selectedStageForComplete.time || "N/A"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStageCompleteDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmStageComplete}
            variant="contained"
            sx={{
              backgroundColor: "#9b42e9",
              "&:hover": { backgroundColor: "#8c5eb4ff" },
            }}
            startIcon={<CheckCircleIcon />}
          >
            Confirm & Add Feedback
          </Button>
        </DialogActions>
      </Dialog>

      {/* Overall Feedback Dialog */}
      <Dialog
        open={overallFeedbackDialogOpen}
        onClose={handleCloseOverallFeedbackDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#6c5ce7",
            color: "white",
          }}
        >
          <CheckCircleIcon sx={{ color: "white" }} />
          <Typography variant="h6">
            Complete Interview & Add Overall Feedback
          </Typography>
          <Box sx={{ flexGrow: 1 }} />

          <CloseIcon
            onClick={handleCloseOverallFeedbackDialog}
            sx={{ cursor: "pointer" }}
          />
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          {selectedInterviewForSchedule && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Candidate:{" "}
                {getCandidateName(selectedInterviewForSchedule.candidate)}
              </Typography>
              <Alert
                severity="info"
                sx={{
                  mb: 2,
                  backgroundColor: "#f3e5f5",
                  color: "#8c5eb4ff",
                  "& .MuiAlert-icon": { color: "#9b42e9" },
                }}
              >
                All interview stages have been completed. Please provide overall
                feedback for this interview process.
              </Alert>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Decision</InputLabel>
                <Select
                  value={overallDecision}
                  label="Decision"
                  onChange={(e) => setOverallDecision(e.target.value)}
                >
                  <MenuItem value="">No change</MenuItem>
                  <MenuItem value="selected">Selected</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="on_hold">On Hold</MenuItem>
                </Select>
              </FormControl>

              <Divider sx={{ my: 1, mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {/* <Alert severity="info" sx={{ py: 0 }}>
                  <strong>The information provided here is intended solely for internal corporate use.</strong>
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Overall Internal Feedback"
                  value={overallFeedback}
                  onChange={(e) => setOverallFeedback(e.target.value)}
                  placeholder="Enter your overall feedback about the candidate's performance across all interview rounds..."
                  required
                /> */}

                {/* <Divider sx={{ my: 1 }} /> */}

                <Box>
                  <Alert

                    severity="warning"
                    sx={{
                      py: 0,
                      mb: 2,
                      backgroundColor: "#FFF4E5",   // soft warning background
                      color: "#663C00",             // readable dark text
                      "& .MuiAlert-icon": {
                        color: "#ED6C02",           // warning orange icon
                      },
                    }}
                  >
                    <strong>
                      The feedback you provide here will be visible to the candidate.
                    </strong>
                  </Alert>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Overall Candidate Feedback"
                    value={overallCandidateFeedback}
                    onChange={(e) =>
                      setOverallCandidateFeedback(e.target.value)
                    }
                    placeholder="Enter any additional comments or feedback for the candidate based on their performance in this interview..."
                    required
                  />
                </Box>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Completed Stages:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {selectedInterviewForSchedule.stages?.map((stage, idx) => (
                    <Chip
                      key={idx}
                      label={`${stage.stageName} - Feedback added: ${stage.candidateStageFeedback &&
                        stage.candidateStageFeedback.trim().length > 0
                        ? "✓"
                        : "Pending"
                        }`}
                      sx={
                        stage.candidateStageFeedback &&
                          stage.candidateStageFeedback.trim().length > 0
                          ? {
                            backgroundColor: "#9b42e9",
                            color: "#FFFFFF",
                          }
                          : {
                            backgroundColor: "#f3e5f5",
                            color: "#9b42e9",
                            border: "1px solid #9b42e9",
                          }
                      }
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOverallFeedbackDialog}>Cancel</Button>
          <Button
            onClick={handleSaveOverallFeedback}
            variant="contained"
            sx={{
              backgroundColor: "#9b42e9",
              "&:hover": { backgroundColor: "#8c5eb4ff" },
            }}
            startIcon={<CheckCircleIcon />}
          >
            Complete Interview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => {
          setActionMenuAnchor(null);
          setSelectedInterviewForAction(null);
        }}
      >
        {selectedInterviewForAction && (
          <>
            {(selectedInterviewForAction.status === "accepted" ||
              selectedInterviewForAction.status === "slot_rejected" ||
              selectedInterviewForAction.status === "slots_proposed" ||
              selectedInterviewForAction.status === "propose_new_slots") && (
                <MenuItem
                  // disabled={selectedInterviewForAction?.stages?.every(
                  //   (s) => s.candidateAction === "selected"
                  // )}
                  onClick={() => {
                    handleOpenProposeSlotDialog(selectedInterviewForAction);
                    setActionMenuAnchor(null);
                    setSelectedInterviewForAction(null);
                  }}
                >
                  <ScheduleIcon sx={{ mr: 1, fontSize: 20 }} />
                  Propose Time Slots
                </MenuItem>
              )}

            {(selectedInterviewForAction.status === "scheduled" ||
              (selectedInterviewForAction.status === "in_progress" &&
                selectedInterviewForAction.stages &&
                selectedInterviewForAction.stages.every((s) =>
                  isStageCompleted(s),
                ))) && (
                <MenuItem
                  onClick={() => {
                    handleOpenScheduleDialog(selectedInterviewForAction);
                    setActionMenuAnchor(null);
                    setSelectedInterviewForAction(null);
                  }}
                >
                  <ScheduleIcon sx={{ mr: 1, fontSize: 20 }} />
                  {selectedInterviewForAction.status === "in_progress"
                    ? "Add New Stage"
                    : "Reschedule Interview"}
                </MenuItem>
              )}

            {!["cancelled", "declined", "admin_declined", "completed"].includes(
              selectedInterviewForAction.status,
            ) && (
                <MenuItem
                  onClick={() => {
                    openConfirm({
                      title: "Cancel interview",
                      message:
                        "Are you sure you want to cancel this interview? This will update the interview status to cancelled.",
                      confirmLabel: "Yes, cancel",
                      cancelLabel: "No, keep it",
                      onConfirm: () => {
                        handleCancelInterview(selectedInterviewForAction);
                        closeConfirm();
                        setActionMenuAnchor(null);
                        setSelectedInterviewForAction(null);
                      },
                    });

                    // Close actions menu immediately after opening confirmation dialog
                    setActionMenuAnchor(null);
                    setSelectedInterviewForAction(null);
                  }}
                >
                  <CancelIcon sx={{ mr: 1, fontSize: 20 }} />
                  Cancel Interview
                </MenuItem>
              )}
            {(selectedInterviewForAction.status === "scheduled" ||
              selectedInterviewForAction.status === "slot_selected") && (
                <MenuItem
                  onClick={() => {
                    handleStartStage(selectedInterviewForAction);
                    setActionMenuAnchor(null);
                    setSelectedInterviewForAction(null);
                  }}
                >
                  <PlayArrowIcon sx={{ mr: 1, fontSize: 20 }} />
                  Start Interview Process
                </MenuItem>
              )}
            {selectedInterviewForAction.status === "in_progress" &&
              selectedInterviewForAction.stages &&
              selectedInterviewForAction.stages.length > 0 &&
              selectedInterviewForAction.stages.some(
                (s) => !isStageCompleted(s),
              ) && (
                <MenuItem
                  onClick={() => {
                    const firstIncompleteStage =
                      selectedInterviewForAction.stages.find((s, idx) => {
                        const hasFeedback =
                          s.candidateStageFeedback &&
                          s.candidateStageFeedback.trim().length > 0;
                        const canAdd = canStartNextStage(
                          selectedInterviewForAction,
                          idx,
                        );
                        return !hasFeedback && canAdd;
                      });
                    if (firstIncompleteStage) {
                      handleOpenFeedbackDialog(
                        selectedInterviewForAction,
                        firstIncompleteStage,
                      );
                    } else {
                      toast.error(
                        "All stages have feedback. Please mark a stage as complete first.",
                      );
                    }
                    setActionMenuAnchor(null);
                    setSelectedInterviewForAction(null);
                  }}
                >
                  <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                  Add Feedback
                </MenuItem>
              )}
            {selectedInterviewForAction.status === "in_progress" &&
              selectedInterviewForAction.stages &&
              selectedInterviewForAction.stages.every(isStageCompleted) && (
                <MenuItem
                  onClick={() => {
                    handleCompleteInterviewWithFeedback(
                      selectedInterviewForAction,
                    );
                    setActionMenuAnchor(null);
                    setSelectedInterviewForAction(null);
                  }}
                >
                  <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                  Complete Interview
                </MenuItem>
              )}
            {selectedInterviewForAction.status === "completed" &&
              selectedInterviewForAction.overallResult !== "selected" &&
              selectedInterviewForAction.overallResult !== "rejected" && (
                <MenuItem
                  onClick={() => {
                    handleOpenDecisionDialog(selectedInterviewForAction);
                    setActionMenuAnchor(null);
                    setSelectedInterviewForAction(null);
                  }}
                >
                  <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                  Set Decision
                </MenuItem>
              )}

            <MenuItem
              onClick={() => {
                handleViewDetails(selectedInterviewForAction);
                setActionMenuAnchor(null);
                setSelectedInterviewForAction(null);
              }}
            >
              <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
              View Full Details
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Decision Dialog */}
      <Dialog
        open={decisionDialogOpen}
        onClose={handleCloseDecisionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "#6c5ce7",
            color: "white",
          }}
        >
          <Typography variant="h6">Set Interview Decision</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <CloseIcon
            onClick={handleCloseDecisionDialog}
            sx={{ cursor: "pointer" }}
          />
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
        >
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Candidate:{" "}
              {selectedInterviewForDecision &&
                getCandidateName(selectedInterviewForDecision.candidate)}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Decision</InputLabel>
              <Select
                value={decisionValue}
                label="Decision"
                onChange={(e) => setDecisionValue(e.target.value)}
              >
                <MenuItem value="selected">Selected</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDecisionDialog}>Cancel</Button>
          <Button
            onClick={handleSaveDecision}
            variant="contained"
            disabled={!decisionValue}
          >
            Save Decision
          </Button>
        </DialogActions>
      </Dialog>

      <ProposedSlots
        open={proposeSlotDialogOpen}
        onClose={handleCloseProposeSlotDialog}
        selectedInterviewForProposeSlot={selectedInterviewForProposeSlot}
        proposedDatesByStage={proposedDatesByStage}
        handleAddDate={handleAddDate}
        handleRemoveDate={handleRemoveDate}
        handleDateChange={handleDateChange}
        handleAddTimeSlot={handleAddTimeSlot}
        handleRemoveTimeSlot={handleRemoveTimeSlot}
        handleTimeSlotChange={handleTimeSlotChange}
        handleStageNameChange={handleStageNameChange}
        handleInterviewerChange={handleInterviewerChange}
        handleAddInterviewer={handleProposeAddInterviewer}
        handleRemoveInterviewer={handleProposeRemoveInterviewer}
        handleAddNewStage={handleAddNewStage}
        handleSaveProposedSlots={handleSaveProposedSlots}
        getCandidateName={getCandidateName}
        today={today}
        setProposedDatesByStage={setProposedDatesByStage}
      />

      {/* Confirm dialog (replaces window.confirm) */}
      <ConfirmDialog
        open={confirmOpen}
        title={confirmProps.title}
        message={confirmProps.message}
        confirmLabel={confirmProps.confirmLabel}
        cancelLabel={confirmProps.cancelLabel}
        onConfirm={() => {
          if (typeof confirmProps.onConfirm === "function")
            confirmProps.onConfirm();
          // ensure dialog closed if onConfirm didn't already
          closeConfirm();
        }}
        onClose={() => {
          // close and do nothing
          closeConfirm();
        }}
      />
      <CareerJourneyPage
        careerJourneyId={selectedCareerJourneyId}
        isOpen={careerDialogOpen}
        isClose={handleCloseCareerJourney}
        hasPeerReview={selectedPeerReview}
      />
    </Box>
  );
};

export default JobApplications;
